import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export type UiLanguage = "en" | "hi" | "kn";

type TranslationDict = Record<string, string>;

type I18nContextValue = {
  lang: UiLanguage;
  setLang: (lang: UiLanguage) => void;
  t: (key: string) => string;
};

const STORAGE_KEY = "homeops.ui.lang";

const TRANSLATIONS: Record<UiLanguage, TranslationDict> = {
  en: {
    "common.cancel": "Cancel",
    "common.close": "Close",
    "common.save": "Save",
    "common.done": "Done",
    "common.add": "Add",
    "common.edit": "Edit",
    "common.delete": "Delete",
    "common.search": "Search",
    "common.clear": "Clear",
    "common.start": "Start",
    "common.end": "End",
    "common.create_failed": "Create failed",
    "common.update_failed": "Update failed",
    "common.delete_failed": "Delete failed",
    "common.missing_session": "Missing session token or household id.",
    "common.unknown": "Unknown",
    "common.unknown_error": "Unknown error",

    "weekday.mon": "Mon",
    "weekday.tue": "Tue",
    "weekday.wed": "Wed",
    "weekday.thu": "Thu",
    "weekday.fri": "Fri",
    "weekday.sat": "Sat",
    "weekday.sun": "Sun",
    "common.loading": "Loading…",
    "common.or": "Or",
    "common.logout": "Logout",
    "common.refresh": "Refresh",
    "nav.dashboard": "Dashboard",
    "nav.home_profile": "Home Profile",
    "nav.chores": "Chores",
    "nav.coverage": "Coverage",
    "nav.events": "Events",
    "nav.recipes": "Recipes",
    "nav.helpers": "Helpers",
    "nav.services": "Services",
    "nav.maintenance": "Maintenance",
    "nav.alerts": "Alerts",
    "nav.automations": "Automations",
    "nav.signals": "Signals",
    "nav.chat": "Chat Assistant",
    "nav.status": "Task Status",
    "nav.admin": "Admin Config",
    "nav.analytics": "Analytics",
    "nav.support": "Support Panel",
    "nav.tests": "Tests",

    "layout.household_manager": "Household Manager",
    "layout.role_demo": "Role (Demo)",
    "layout.role.household": "Household User",
    "layout.role.admin": "Admin",
    "layout.role.owner": "Owner / Dev",
    "layout.role.support": "Support Staff",
    "layout.agent_setup": "Agent Setup",
    "layout.profile": "Profile",
    "layout.send_feedback": "Send feedback",
    "layout.chat": "Chat",
    "layout.chat_assistant": "Chat Assistant",

    "signals.title": "Signals",
    "signals.subtitle": "Household events and feedback captured from chat and automations",
    "signals.upcoming_events": "Upcoming events",
    "signals.no_upcoming_events": "No upcoming events",
    "signals.cleaning_feedback": "Cleaning feedback",
    "signals.no_cleaning_feedback": "No cleaning feedback yet",
    "signals.rating": "Rating",

    "automations.title": "Automations",
    "automations.subtitle": "Manage scheduled reminders and proactive alerts",
    "automations.summary": "Active {active} • Paused {paused} • Disabled {disabled}",
    "automations.none_title": "No automations yet",
    "automations.none_body": "Approve an automation suggestion from Chat to create scheduled alerts.",
    "automations.cadence": "Cadence",
    "automations.next_run": "Next run",
    "automations.last_run": "Last run",
    "automations.run_now": "Run now",
    "automations.pause": "Pause",
    "automations.resume": "Resume",
    "automations.disable": "Disable",
    "automations.edit": "Edit automation",
    "automations.field_title": "Title",
    "automations.field_description": "Description",
    "automations.field_cadence": "Cadence",
    "automations.field_at_time": "At time (optional)",
    "automations.field_day_of_week": "Day of week (weekly)",
    "automations.field_day_of_month": "Day of month (monthly)",
    "automations.cadence_daily": "Daily",
    "automations.cadence_weekdays": "Weekdays",
    "automations.cadence_weekly": "Weekly",
    "automations.cadence_monthly": "Monthly",
    "automations.cadence_hourly": "Hourly",
    "automations.cadence_every_2_hours": "Every 2 hours",
    "automations.cadence_every_5_minutes": "Every 5 minutes",
    "automations.status_active": "Active",
    "automations.status_paused": "Paused",
    "automations.status_disabled": "Disabled",
    "automations.title_required": "Title is required",
    "automations.weekly_requires_dow": "Weekly cadence requires a day_of_week",
    "automations.monthly_requires_dom": "Monthly cadence requires a day_of_month",

    "auth.login.welcome_back": "Welcome Back",
    "auth.login.subtitle": "Sign in to your Home Operations Manager",
    "auth.login.email": "Email",
    "auth.login.password": "Password",
    "auth.login.remember_me": "Remember me",
    "auth.login.forgot_password": "Forgot password?",
    "auth.login.sign_in": "Sign In",
    "auth.login.google": "Google",
    "auth.login.microsoft": "Microsoft",
    "auth.login.no_account": "Don't have an account?",
    "auth.login.sign_up": "Sign up",

    "auth.signup.create_account": "Create Account",
    "auth.signup.subtitle": "Join your household or start managing your home",
    "auth.signup.full_name": "Full Name",
    "auth.signup.household_code": "Household Code (Optional)",
    "auth.signup.household_code_placeholder": "Enter existing household code",
    "auth.signup.household_code_help": "Leave empty to create a new household",
    "auth.signup.confirm_password": "Confirm Password",
    "auth.signup.terms": "I agree to the Terms of Service and Privacy Policy",
    "auth.signup.create": "Create Account",
    "auth.signup.have_account": "Already have an account?",
    "auth.signup.sign_in": "Sign in",

    "dashboard.title": "Dashboard",
    "dashboard.subtitle": "Welcome back! Here's what's happening in your home.",
    "dashboard.setup_incomplete": "Your home setup is incomplete",
    "dashboard.continue_setup": "Continue Setup",
    "dashboard.setup_home_profile": "Home profile",
    "dashboard.setup_features": "Home features",
    "dashboard.setup_chores": "Chores",
    "dashboard.setup_helpers": "Helpers",
    "dashboard.assign_chores_title": "Chores need assignment",
    "dashboard.assign_chores_hint": "{count} chores are not yet assigned to any helper.",
    "dashboard.assign_chores_button": "Assign to helpers",
    "dashboard.upcoming_chores": "Upcoming Chores",
    "dashboard.tasks_this_week": "Tasks this week",
    "dashboard.view_all": "View All",
    "dashboard.no_upcoming_chores": "No upcoming chores.",
    "dashboard.recent_alerts": "Recent Alerts",
    "dashboard.notifications_and_reminders": "Notifications and reminders",
    "dashboard.progress_title": "March Progress",
    "dashboard.progress_subtitle": "Task completion this month",
    "dashboard.completed": "Completed",
    "dashboard.in_progress": "In Progress",
    "dashboard.overdue": "Overdue",
    "dashboard.quick_actions": "Quick Actions",
    "dashboard.add_chore": "Add Chore",
    "dashboard.add_recipe": "Add Recipe",
    "dashboard.chat_assistant": "Chat Assistant",
    "dashboard.view_status": "View Status",

    "tests.title": "Tests",
    "tests.subtitle": "Run and schedule your automated test suites",
    "tests.run_now": "Run now",
    "tests.run_now_help": "Start a test run and track results and logs",
    "tests.run_vitest": "Run Vitest",
    "tests.run_vitest_watch": "Vitest (watch)",
    "tests.run_vitest_ui": "Vitest UI",
    "tests.run_playwright": "Run Playwright",
    "tests.run_playwright_ui": "Playwright UI",
    "tests.schedules": "Schedules",
    "tests.schedules_help": "Cron schedules are executed by your local test runner service",
    "tests.schedule_name": "Schedule name",
    "tests.cron_expr": "Cron expression",
    "tests.type": "Type",
    "tests.mode": "Mode",
    "tests.add_schedule": "Add schedule",
    "tests.no_schedules": "No schedules yet.",
    "tests.last_run": "Last run",
    "tests.enable": "Enable",
    "tests.disable": "Disable",
    "tests.recent_runs": "Recent runs",
    "tests.recent_runs_help": "Recent test runs captured by the local runner",
    "tests.no_runs": "No runs yet.",
    "tests.created": "Created",
    "tests.started": "Started",
    "tests.finished": "Finished",
    "tests.view_log": "View log",
    "tests.view_report": "View report",
    "tests.cancel": "Cancel",

    "recipes.title": "Recipes",
    "recipes.subtitle": "Your family's favorite meals and cooking ideas",
    "recipes.add_recipe": "Add Recipe",
    "recipes.youtube_search": "YouTube Recipe Search",
    "recipes.youtube_search_subtitle": "Search recipes on YouTube with customizable query options",
    "recipes.search_options": "Search Options",
    "recipes.recipe": "Recipe",
    "recipes.generated_query": "Generated query",
    "recipes.search_youtube": "Search on YouTube",
    "recipes.use_current_search": "Use current page search",
    "recipes.search_placeholder": "Search recipes by name or cuisine...",
    "recipes.category": "Category",
    "recipes.all": "All",
    "recipes.breakfast": "Breakfast",
    "recipes.lunch": "Lunch",
    "recipes.dinner": "Dinner",
    "recipes.dessert": "Dessert",
    "recipes.ingredients": "Ingredients:",
    "recipes.more": "more...",
    "recipes.view_full": "View Full Recipe",
    "recipes.no_recipes": "No recipes found",
    "recipes.no_recipes_hint": "Try adjusting your search or add a new recipe",
    "recipes.add_new_recipe": "Add New Recipe",
    "recipes.recipe_title": "Recipe Title",
    "recipes.description": "Description",
    "recipes.cuisine": "Cuisine",
    "recipes.prep_time": "Prep Time",
    "recipes.cook_time": "Cook Time",
    "recipes.servings": "Servings",
    "recipes.instructions": "Instructions",
    "recipes.save_recipe": "Save Recipe",
    "recipes.youtube_options": "YouTube Search Options",
    "recipes.query_template": "Query template",
    "recipes.preferred_channels": "Preferred channels / keywords (optional)",
    "recipes.include_shorts": "Include Shorts",
    "recipes.no": "No",
    "recipes.yes": "Yes",
    "recipes.tab_recipes": "Recipes",
    "recipes.tab_meal_plan": "Meal Planner",
    "recipes.tab_shopping_list": "Shopping List",
    "recipes.edit_recipe": "Edit Recipe",
    "recipes.source_url": "Source URL",
    "recipes.ingredients_hint": "One ingredient per line",
    "recipes.prev_week": "Prev week",
    "recipes.next_week": "Next week",
    "recipes.assign_meal": "Plan a Meal",
    "recipes.date": "Date",
    "recipes.meal_type": "Meal",
    "recipes.pick_recipe": "Pick a recipe",
    "recipes.or": "or",
    "recipes.custom_meal": "Custom meal",
    "recipes.notes": "Notes",
    "recipes.shopping_list_hint": "Items needed for this week's meal plan, compared against your pantry",
    "recipes.shopping_list_empty": "No items yet. Plan meals for the week to generate a shopping list.",

    "alerts.no_alerts": "No alerts",
    "alerts.caught_up": "You're all caught up!",
    "alerts.title": "Alerts & Notifications",
    "alerts.subtitle": "Stay informed about household activities and reminders",
    "alerts.unread": "Unread",
    "alerts.mark_all_read": "Mark All Read",
    "alerts.all": "All",
    "alerts.critical_tab": "Critical",
    "alerts.warning_tab": "Warning",
    "alerts.info_tab": "Info",
    "alerts.create_custom": "Create Custom Alert",
    "alerts.alert_title": "Alert Title",
    "alerts.message": "Message",
    "alerts.priority": "Priority",
    "alerts.category": "Category",
    "alerts.critical": "Critical",
    "alerts.warning": "Warning",
    "alerts.info": "Info",
    "alerts.maintenance": "Maintenance",
    "alerts.bills": "Bills",
    "alerts.inventory": "Inventory",
    "alerts.safety": "Safety",
    "alerts.reminders": "Reminders",
    "alerts.alert_date_time": "Alert Date & Time",
    "alerts.create": "Create Alert",

    "admin.title": "Admin Configuration",
    "admin.subtitle": "Manage household settings and preferences",
    "admin.general": "General",
    "admin.members": "Members",
    "admin.notifications": "Notifications",
    "admin.security": "Security",
    "admin.integrations": "Integrations",
    "admin.agents": "Agents",
    "admin.agents_subtitle": "Manage built-in agents (household overrides)",
    "admin.agents_empty": "No agents found",
    "admin.agent_enabled": "Enabled",
    "admin.agent_key": "Key",
    "admin.agent_display_name": "Display name",
    "admin.agent_model": "Model (optional)",
    "admin.agent_system_prompt": "System prompt",
    "admin.agent_tool_allowlist": "Tool allowlist (comma-separated)",
    "admin.agent_tool_allowlist_help": "Example: db.select, db.insert",
    "admin.members_subtitle": "View household members",
    "admin.members_empty": "No members found",
    "admin.notifications_subtitle": "Notification preferences",
    "admin.notifications_enabled": "Notifications enabled",
    "admin.email_digest": "Email digest",
    "admin.security_subtitle": "Security preferences",
    "admin.two_factor_auth": "Two-factor authentication",
    "admin.auto_assign_chores": "Auto-assign chores",
    "admin.integrations_subtitle": "Integration settings",
    "admin.youtube_settings": "YouTube settings",
    "admin.recipe_settings": "Recipe settings",
    "admin.allowed_sources": "Allowed sources (comma-separated)",
    "admin.min_rating": "Minimum rating",
    "admin.min_reviews": "Minimum reviews",
    "admin.lenient_missing_reviews": "Lenient when reviews missing",
    "admin.invite_email": "Invite email",
    "admin.invite_role": "Invite role",
    "admin.role_member": "Member",
    "admin.role_admin": "Admin",
    "admin.role_owner": "Owner",
    "admin.send_invite": "Send Invite",
    "admin.pending_invites": "Pending invites",
    "admin.invites_empty": "No invites",
    "admin.copy_invite_link": "Copy link",
    "admin.revoke_invite": "Revoke",
    "admin.invite_status_pending": "Pending",
    "admin.invite_status_accepted": "Accepted",
    "admin.invite_status_revoked": "Revoked",

    "invite.accept.title": "Accept invite",
    "invite.accept.subtitle": "Join a household",
    "invite.accept.missing_token": "Missing invite token",
    "invite.accept.working": "Accepting invite…",
    "invite.accept.success": "Invite accepted. You are now a member of the household.",
    "invite.accept.go_home": "Go to dashboard",
    "admin.household_information": "Household Information",
    "admin.household_information_subtitle": "Basic information about your household",
    "admin.household_name": "Household Name",
    "admin.address": "Address",
    "admin.timezone": "Timezone",
    "admin.language": "Language",
    "admin.save_changes": "Save Changes",

    "support.tickets": "Support Tickets",
    "support.subheader": "All user support requests",
    "support.title": "Support Panel",
    "support.subtitle": "Manage and respond to user support requests",
    "support.open_tickets": "Open Tickets",
    "support.in_progress": "In Progress",
    "support.resolved_today": "Resolved Today",
    "support.avg_response_time": "Avg. Response Time",
    "support.search_placeholder": "Search tickets...",
    "support.all": "All",
    "support.open": "Open",
    "support.resolved": "Resolved",
    "support.type_response": "Type your response...",

    "helpers.title": "Helpers & Services",
    "helpers.subtitle": "Manage household service providers",
    "helpers.add_helper": "Add Helper",
    "helpers.onboard_helper": "Onboard Helper",
    "helpers.empty_title": "No helpers yet",
    "helpers.empty_subtitle": "Add your first helper to start managing household staff.",
    "helpers.add_new_helper": "Add New Helper",
    "helpers.name_business": "Name / Business Name",
    "helpers.name_required": "Name is required.",
    "helpers.role_service_type": "Role / Service Type",
    "helpers.phone": "Phone",
    "helpers.notes": "Notes",
    "helpers.adding": "Adding…",
    "helpers.call": "Call",
    "helpers.capacity": "Capacity",
    "helpers.schedule": "Schedule",
    "helpers.time_off": "Time off",
    "helpers.hours_per_day": "Hours per day",
    "helpers.hours_per_day_suffix": "h/day",

    "chat.review_home_profile": "Review home profile",
    "chat.recommended_chores": "Recommended chores",
    "chat.recommended_chores_help": "Select what you want, edit only if needed, then submit.",
    "chat.recommended_automations": "Recommended automations",
    "chat.recommended_automations_help": "Review and approve to turn them into scheduled alerts.",
    "chat.toggle_all": "Toggle all",
    "chat.submit": "Submit",
    "chat.edit_chore": "Edit chore",
    "chat.chore_title": "Title",
    "chat.chore_description": "Description",
    "chat.frequency": "Frequency",
    "chat.frequency_not_set": "Not set",
    "chat.frequency_daily": "Daily",
    "chat.frequency_weekly": "Weekly",
    "chat.frequency_biweekly": "Biweekly",
    "chat.frequency_monthly": "Monthly",
    "chat.due": "Due",
    "chat.helper": "Helper",
    "chat.unassigned": "Unassigned",
    "chat.approve": "Approve",
    "chat.reject": "Reject",
    "chat.apply": "Apply",
    "chat.create_table": "Create {table}",
    "chat.tool_execution_failed": "Tool execution failed",
    "chat.executed_tool": "Executed {tool}.",
    "chat.create_home_profile_first": "Please create your home profile first, then generate recommendations.",
    "chat.could_not_fetch": "Couldn’t fetch the information",
    "chat.transliteration_off": "ABC",
    "helpers.helper_default_type": "Helper",
    "helpers.helper_schedule": "Helper schedule",
    "helpers.helper_capacity": "Helper capacity",
    "helpers.helper_time_off": "Helper time off",
    "helpers.add_time_off": "Add time off",
    "helpers.recent_time_off": "Recent time off",
    "helpers.no_time_off": "No time off recorded.",
    "helpers.on_leave": "On leave",
    "helpers.helper_on_leave": "Helper is on leave.",
    "helpers.reason": "Reason",
    "helpers.feedback": "Feedback",
    "helpers.view_feedback": "View feedback",
    "helpers.rating": "Rating",
    "helpers.comment_optional": "Comment (optional)",
    "helpers.submit_feedback": "Submit feedback",
    "helpers.recent_feedback": "Recent feedback",
    "helpers.no_feedback": "No feedback yet.",
    "helpers.feedback_submitted": "Feedback submitted.",
    "helpers.rewards": "Rewards",
    "helpers.add_reward": "Add reward",
    "helpers.recent_rewards": "Recent rewards",
    "helpers.no_rewards": "No rewards yet.",
    "helpers.quarter": "Quarter",
    "helpers.reward_type": "Reward type",
    "helpers.amount_optional": "Amount (optional)",
    "helpers.currency_optional": "Currency (optional)",
    "helpers.reward_reason_optional": "Reason (optional)",
    "helpers.submit_reward": "Create reward",
    "helpers.reward_created": "Reward created.",
    "helpers.no_days": "No days",
    "helpers.enter_valid_times": "Enter valid start/end times (HH:MM).",
    "helpers.enter_valid_hours": "Enter a valid hours/day value (0-24).",
    "helpers.helper_schedule_updated": "Helper schedule updated.",
    "helpers.helper_capacity_updated": "Helper capacity updated.",
    "helpers.helper_created": "Helper created.",
    "helpers.start_end_required": "Start and end are required.",
    "helpers.time_off_added": "Time off added.",
    "helpers.category.all": "All",
    "helpers.category.cleaning": "Cleaning",
    "helpers.category.maintenance": "Maintenance",
    "helpers.category.outdoor": "Outdoor",
    "helpers.category.childcare": "Childcare",
    "helpers.category.technology": "Technology",
    "helpers.more_actions": "More actions",
    "helpers.delete_helper": "Delete helper",
    "helpers.delete_helper_title": "Delete helper",
    "helpers.delete_helper_confirm": "Are you sure you want to delete this helper?",
    "helpers.helper_deleted": "Helper deleted.",
    "helpers.language": "Language",
    "helpers.language_short": "Lang",
    "helpers.set_language": "Set language",
    "helpers.set_language_title": "Set helper language",
    "helpers.language_updated": "Helper language updated.",
    "helpers.lang.en": "English",
    "helpers.lang.hi": "हिंदी",
    "helpers.lang.kn": "ಕನ್ನಡ",

    "home_profile.dialog_title": "Home Profile",
    "home_profile.step_home_type": "Home Type",
    "home_profile.step_rooms": "Rooms",
    "home_profile.step_household": "Household",
    "home_profile.step_review": "Review",
    "home_profile.view_saved": "Here's what's saved for your home.",
    "home_profile.pick_type": "Choose your home type — rooms and floors will be pre-filled and you can edit them next.",
    "home_profile.rooms_hint": "Edit room names or assign floors. Display names are what helpers and the assistant will use.",
    "home_profile.household_hint": "Household context — helps personalise schedules and recommendations.",
    "home_profile.review_hint": "Review your home profile. Go back to edit anything.",
    "home_profile.field_area": "Total area (sq ft)",
    "home_profile.field_floors": "Number of floors",
    "home_profile.field_floor": "Floor",
    "home_profile.field_display_name": "Display Name",
    "home_profile.field_template_name": "Template Name",
    "home_profile.field_bathrooms": "Bathrooms",
    "home_profile.field_flooring": "Flooring type (optional)",
    "home_profile.field_flooring_placeholder": "e.g. Marble, Tile, Wood",
    "home_profile.add_room": "Add a room",
    "home_profile.add_room_placeholder": "Type a name or pick a suggestion",
    "home_profile.room_name": "Room name",
    "home_profile.rename_room": "Rename room",
    "home_profile.no_rooms_yet": "No rooms added yet. Use the form below to add rooms.",
    "home_profile.add": "Add",
    "home_profile.label_type": "Type",
    "home_profile.label_area": "Area",
    "home_profile.label_sq_ft": "sq ft",
    "home_profile.label_floors": "Floors",
    "home_profile.label_bathrooms": "Bathrooms",
    "home_profile.label_pets": "Pets",
    "home_profile.label_kids": "Kids",
    "home_profile.label_flooring": "Flooring",
    "home_profile.label_yes": "Yes",
    "home_profile.label_no": "No",
    "home_profile.label_bhk": "BHK",
    "home_profile.floor_ground": "Ground Floor",
    "home_profile.floor_unassigned": "Unassigned",
    "home_profile.sq_ft_up_to": "< {max} sq ft",
    "home_profile.sq_ft_from": "{min}+ sq ft",
    "home_profile.sq_ft_range": "{min}–{max} sq ft",
    "home_profile.rooms_count": "{n} rooms · {floors} floor",
    "home_profile.rooms_count_plural": "{n} rooms · {floors} floors",
    "home_profile.pets_label": "Pets",
    "home_profile.kids_label": "Kids",
    "home_profile.discard": "Discard",
    "home_profile.back": "Back",
    "home_profile.next": "Next",
    "home_profile.save": "Save",
    "home_profile.edit": "Edit",
    "home_profile.close": "Close",
    "home_profile.subtitle": "Manage your home's rooms, layout, and household details.",
    "home_profile.empty_title": "No home profile yet",
    "home_profile.create_button": "Create Home Profile",
    "home_profile.draft_found": "You have unsaved changes from a previous session. Restore them or discard?",
    "home_profile.draft_restore": "Restore",
    "home_profile.draft_discard": "Discard",
    "home_profile.draft_restored": "Unsaved changes restored. Continue editing and click Save.",

    "onboarding.welcome_title": "Welcome to HomeOps",
    "onboarding.welcome_subtitle": "Your AI-powered home management assistant",
    "onboarding.welcome_bullet_1": "Manage chores, helpers, and schedules effortlessly",
    "onboarding.welcome_bullet_2": "AI agent handles coordination and adjustments automatically",
    "onboarding.welcome_bullet_3": "Supports English, Hindi, and Kannada",
    "onboarding.get_started": "Let's get started",
    "onboarding.step_welcome": "Welcome",
    "onboarding.step_profile": "Your Profile",
    "onboarding.step_home": "Your Home",
    "onboarding.step_agent": "Meet the Agent",
    "onboarding.profile_name_title": "Tell us about yourself",
    "onboarding.profile_name_label": "Your name",
    "onboarding.role_title": "What's your role at home?",
    "onboarding.role_subtitle": "This helps the agent understand who manages what.",
    "onboarding.role_primary_manager": "Primary Manager",
    "onboarding.role_primary_manager_desc": "I run the household day-to-day",
    "onboarding.role_shared_responsibility": "Shared Responsibility",
    "onboarding.role_shared_responsibility_desc": "We share household duties equally",
    "onboarding.role_contributor": "Contributor",
    "onboarding.role_contributor_desc": "I help with specific tasks",
    "onboarding.role_observer": "Observer",
    "onboarding.role_observer_desc": "I mostly track what's happening",
    "onboarding.goals_title": "What matters most to you?",
    "onboarding.goals_subtitle": "Select one or more priorities. The agent will tailor recommendations.",
    "onboarding.goal_cleanliness": "Cleanliness",
    "onboarding.goal_health_nutrition": "Health & Nutrition",
    "onboarding.goal_cost_optimization": "Cost Optimization",
    "onboarding.goal_time_saving": "Time Saving",
    "onboarding.language_title": "Preferred language",
    "onboarding.language_subtitle": "Choose the language you'd like to use with the assistant.",
    "onboarding.schedule_title": "Your availability",
    "onboarding.schedule_subtitle": "When are you usually free? This is optional — helps the agent plan around your schedule.",
    "onboarding.schedule_hours": "Work hours",
    "onboarding.schedule_hours_help": "e.g. 9-18 means 9 AM to 6 PM",
    "onboarding.schedule_skip": "Skip this step",
    "onboarding.agent_title": "Meet your Home Assistant",
    "onboarding.agent_subtitle": "Your AI agent can help you with all of these and more:",
    "onboarding.agent_example_1": "\"Set up daily cleaning for all rooms\"",
    "onboarding.agent_example_2": "\"Assign the cook to prepare lunch on weekdays\"",
    "onboarding.agent_example_3": "\"My helper is on leave next week — adjust the schedule\"",
    "onboarding.agent_example_4": "\"What chores are due today?\"",
    "onboarding.finish": "Start using HomeOps",
    "onboarding.skip_all": "Skip for now",
    "onboarding.name_hint": "This helps your agent personalize the experience.",
    "onboarding.your_name": "Your name",
    "onboarding.continue_to_agent": "Continue — meet your agent",
    "onboarding.confirm_selection": "Confirm selection",
    "onboarding.confirm_rooms": "Confirm rooms",
    "onboarding.select_features": "What does your home have?",
    "onboarding.confirm_features": "Confirm features",
    "onboarding.confirm_details": "Confirm details",
    "onboarding.review_chores": "Review recommended chores",
    "onboarding.create_chores": "Create chores",
    "onboarding.review_chores_hint": "Edit titles and frequencies, uncheck chores you don't need.",
    "onboarding.skip_chores": "Skip for now",
    "onboarding.add_helper": "Add your household helpers",
    "onboarding.add_helpers_hint": "Add all your helpers, then save them together.",
    "onboarding.add_to_list": "Add to list",
    "onboarding.save_helpers": "Save helpers",
    "onboarding.skip_helpers": "No helpers / skip",
    "onboarding.setup_progress": "Setup Progress",
    "onboarding.skip_step": "Skip this step",
    "onboarding.finish_later": "Finish later",
    "onboarding.setup_complete": "Setup complete!",
    "onboarding.setup_complete_hint": "Your home is set up. You can always adjust things from the sidebar.",
    "onboarding.assign_chores_now": "Assign chores to helpers",
    "onboarding.assign_later": "I'll do this later",

    "coverage.title": "Coverage",
    "coverage.subtitle": "See what's covered by helpers, devices, and where the gaps are",
    "coverage.map_title": "Coverage Map",
    "coverage.map_subtitle": "Spaces by cadence — colored by who covers them",
    "coverage.gaps_title": "Coverage Gaps",
    "coverage.gaps_subtitle": "Spaces with no chore assignments and no automation",
    "coverage.empty_title": "No coverage data yet",
    "coverage.empty_subtitle": "Use the Coverage Planner to walk through your home space-by-space and generate a personalized chore list.",
    "coverage.first_time_title": "Set up your initial chores",
    "coverage.first_time_subtitle": "We found {count} space(s) in your home profile. Use the planner to create your initial chore list — you can fine-tune later.",
    "coverage.audit_button": "Audit Coverage",
    "coverage.audit_view": "Coverage Audit",
    "coverage.matrix_view": "Full Matrix",
    "coverage.bulk_add_button": "Bulk Add",

    "coverage_audit.title": "Coverage Audit",
    "coverage_audit.subtitle": "See what's missing and add only what you need",
    "coverage_audit.gaps_section": "Gaps to fix",
    "coverage_audit.full_map_title": "Show full coverage map",
    "coverage_audit.no_spaces": "No spaces found in your home profile yet. Set up your home profile first to start auditing coverage.",

    "health.title": "Your Home Coverage",
    "health.score_label": "score",
    "health.summary": "{total} room(s) — {covered} fully covered, {partial} partial, {gaps} with gaps",
    "health.fully_covered": "Fully covered",
    "health.partial": "Partial",
    "health.gaps": "Gaps",
    "health.fix_all": "Fix all {count} gap(s)",
    "health.critical_gaps_label": "{count} critical",

    "gaps.severity_critical": "Critical",
    "gaps.severity_important": "Important",
    "gaps.severity_nice": "Nice to have",
    "gaps.critical_intro": "These gaps affect daily hygiene and safety. Recommended to fix first.",
    "gaps.empty_title": "All covered!",
    "gaps.empty_subtitle": "Every recommended chore is in place. Your home is well covered.",
    "gaps.chore_title": "Chore title",
    "gaps.add_chore": "Add chore",
    "gaps.dismiss": "Not needed",
    "gaps.added_success": "Added \"{title}\" to your chores.",
    "gaps.dismissed_success": "Got it — we won't suggest this again.",
    "gaps.fix_all_success": "Created {count} chore(s).",
    "gaps.fix_all_partial": "Added {added} chore(s), {failed} failed.",

    "helper_daily.empty_hint": "No chores scheduled for this date. Use Sync Schedule to generate chores, or switch to the list view to see all chores.",

    "engine.sync_schedule": "Sync Schedule",
    "engine.syncing": "Syncing…",
    "engine.sync_results": "Schedule Sync",
    "engine.sync_summary": "{scheduled} new chore(s) to schedule, {adjusted} adjustment(s) recommended",
    "engine.section_scheduled": "New chores to schedule",
    "engine.section_adjustments": "Adjustments",
    "engine.apply": "Apply",
    "engine.applied": "Applied",
    "engine.apply_all": "Apply all ({count})",
    "engine.edit_title": "Chore title",
    "engine.nothing_to_sync": "Everything is up to date. No changes needed.",
    "engine.tasks_across_rooms": "across {rooms} room(s)",
    "engine.apply_room": "Apply room",
    "engine.escalation_title": "Intervention needed — tasks at risk",

    "planner.empty_coverage": "No spaces detected yet. Use the planner to set up your spaces and get a personalized chore list.",

    "events.title": "Household Events",
    "events.subtitle": "Track guests, vacations, occasions, and other events that affect chores",
    "events.add_event": "Add Event",
    "events.empty": "No events yet. Add a guest visit, vacation, or special occasion.",
    "events.type": "Type",
    "events.start_at": "Start date/time",
    "events.end_at": "End date/time",
    "events.end_at_optional": "Optional",
    "events.notes": "Notes",
    "events.type_guest_arrival": "Guest arrival",
    "events.type_vacation": "Vacation",
    "events.type_occasion": "Special occasion",
    "events.type_weather": "Weather event",
    "events.type_member_health": "Member health",
    "events.type_helper_leave": "Helper leave",
    "events.replan_hint": "Event saved — chore suggestions may have changed",

    "helpers.workload_title": "Helper Workload",
    "helpers.workload_subtitle": "Today's assignments and capacity utilization",
    "helpers.overdue": "overdue",
    "helpers.no_capacity_set": "Daily capacity not set",
    "helpers.over_capacity": "Over capacity — consider rebalancing",

    "chores.bulk_reassign_title": "Bulk Reassign Chores",
    "chores.bulk_reassign_subtitle": "Reassign chores currently assigned to {name}",
    "chores.reassign_to": "Reassign to",
    "chores.no_chores_to_reassign": "No active chores to reassign",
    "chores.selected_count": "{count} of {total} selected",
    "chores.bulk_reassign_apply": "Reassign {count}",

    "chores.title": "Chores",
    "chores.subtitle": "Manage household chores and assignments",
    "chores.back_to_coverage": "Back to coverage",
    "chores.view_all": "View all chores",
    "chores.daily_chores": "Daily chores",
    "chores.add_chore": "Add chore",
    "chores.remove_duplicates": "Remove duplicates",
    "chores.coverage": "Coverage",
    "chores.planned_frequency": "Planned frequency by space",
    "chores.edit_baseline": "Edit baseline",
    "chores.autofill_missing": "Auto-fill missing coverage",
    "chores.no_chores_yet": "No chores yet.",
    "chores.daily": "Daily",
    "chores.weekly": "Weekly",
    "chores.biweekly": "Biweekly",
    "chores.monthly": "Monthly",
    "chores.assign_to": "Assign to",
    "chores.unassigned": "Unassigned",
    "chores.current": "Current",
    "chores.due": "Due",
    "chores.no_duplicates_found": "No duplicates found.",
    "chores.no_localization_needed": "No chore needs localization.",
    "chores.localized": "Localized",
    "chores.chore_s": "chore(s).",
    "chores.removed": "Removed",
    "chores.duplicate_chore_s": "duplicate chore(s).",
    "chores.assigned_to": "Assigned to",
    "chores.reported_makeup_created": "Reported. Make-up chore created.",
    "chores.baseline_updated": "Baseline updated.",
    "chores.missing_baseline_draft": "Missing baseline draft.",
    "chores.no_home_profile": "No home profile found. Create your home profile first (Chat → Home Profile), then try again.",
    "chores.aria_report_not_done": "Report as not done",
    "chores.aria_edit_chore": "Edit chore",
    "chores.date": "Date",
    "chores.time_zone": "Time zone",
    "chores.only_daily_cadence": "Daily cadence only",
    "chores.daily_counts": "Daily:",
    "chores.inferred_daily_counts": "Estimated daily:",
    "chores.no_chores_found": "No chores found",
    "chores.no_chores_due_on_date": "No chores due on this date.",
    "chores.filters": "Filters",
    "chores.caught_up": "You're all caught up!",
    "chores.create_new": "Create new chore",
    "chores.chore_title": "Chore title",
    "chores.description": "Description",
    "chores.category": "Category",
    "chores.due_date": "Due date",
    "chores.create_chore": "Create chore",
    "chores.chore_updated": "Chore updated.",
    "chores.no_recommendations_selected": "No recommendations selected.",
    "chores.created": "Created",
    "chores.makeup_already_exists": "A make-up chore already exists for this chore.",
    "chores.edit_coverage_baseline": "Edit coverage baseline",
    "chores.edit_baseline_help": "Pick target cadences per space type. The coverage heatmap highlights missing target chores.",
    "chores.save_baseline": "Save baseline",
    "chores.autofill_help": "Review recommended chores to improve coverage of key spaces (kitchen, bathrooms, balcony, terrace, garage, etc.).",
    "chores.prepared": "Prepared",
    "chores.recommendation_s": "recommendation(s).",
    "chores.no_missing_coverage": "No missing coverage found.",
    "chores.create_chores": "Create chores",
    "chores.localize_title": "Localize chore wording",
    "chores.localize_help": "This updates chore titles/descriptions to match Indian context (e.g., \"vacuum\" → \"sweep and mop\").",
    "chores.this_will_update": "This will update",
    "chores.no_chores_need_localization": "No chore needs localization.",
    "chores.update_chores": "Update chores",
    "chores.edit_chore": "Edit chore",
    "chores.title_label": "Title",
    "chores.status": "Status",
    "chores.status_pending": "pending",
    "chores.status_in_progress": "in-progress",
    "chores.status_completed": "completed",
    "chores.priority": "Priority (1-3)",
    "chores.space": "Space",
    "chores.subspace": "Subspace",
    "chores.cadence": "Cadence",
    "chores.none": "(none)",
    "chores.helper": "Helper",
    "chores.remove_duplicate_chores": "Remove duplicate chores",
    "chores.duplicate_rule": "Duplicate rule: same title + space + cadence.",
    "chores.keep_strategy": "Strategy: keep the most recently completed if any, otherwise keep the most recent.",
    "chores.permanently_delete_prefix": "This will permanently delete",
    "chores.across": "across",
    "chores.duplicate_group_s": "duplicate group(s).",
    "chores.delete_duplicates": "Delete duplicates",
    "chores.helper_view": "Helper view",
    "chores.task_view": "Task view",
    "chores.report_not_done": "Report not done",
    "chores.priority_low": "P1 — Low",
    "chores.priority_medium": "P2 — Medium",
    "chores.priority_high": "P3 — High",
    "chores.n_selected": "{count} selected",
    "chores.select_all": "Select all",
    "chores.delete_selected": "Delete selected",
    "chores.confirm_delete": "Delete \"{title}\"? This cannot be undone.",
    "chores.confirm_bulk_delete_title": "Delete {count} chore(s)?",
    "chores.confirm_bulk_delete_message": "This will permanently delete the selected chores.",
    "chores.choose_space": "Choose a space",
    "chores.choose_space_error": "Please choose a space.",
    "chores.apply": "Apply",
    "chores.clear_filters": "Clear filters",
    "chores.tab_all": "All",
    "chores.tab_pending": "Pending",
    "chores.tab_in_progress": "In Progress",
    "chores.tab_completed": "Completed",
    "chores.empty_title": "No chores found",
    "chores.empty_subtitle": "Add a chore or run the scheduler to get started.",
    "chores.on_leave_warning": "Helper is on leave for this date",

    "replan.title": "Smart Suggestions",
    "replan.subtitle": "{count} suggestion(s) based on events, coverage, and workload",
    "replan.button": "Suggestions",
    "replan.empty": "No suggestions right now. Add household events or check coverage to get started.",
    "replan.dismiss": "Dismiss",
    "replan.severity_info": "Info",
    "replan.severity_warning": "Warning",
    "replan.severity_critical": "Critical",
    "replan.kind_reassign_helper": "Reassign",
    "replan.kind_add_chore": "Add chore",
    "replan.kind_skip_chore": "Skip",
    "replan.kind_balance_workload": "Rebalance",
    "replan.kind_coverage_gap": "Coverage",

  },
  hi: {
    "helpers.title": "हेल्पर्स और सेवाएँ",
    "helpers.subtitle": "घरेलू सेवा प्रदाताओं का प्रबंधन करें",
    "helpers.add_helper": "हेल्पर जोड़ें",
    "helpers.onboard_helper": "हेल्पर ऑनबोर्ड करें",
    "helpers.empty_title": "अभी तक कोई हेल्पर नहीं",
    "helpers.empty_subtitle": "घरेलू स्टाफ का प्रबंधन शुरू करने के लिए अपना पहला हेल्पर जोड़ें।",
    "helpers.add_new_helper": "नया हेल्पर जोड़ें",
    "helpers.name_business": "नाम / व्यवसाय का नाम",
    "helpers.name_required": "नाम आवश्यक है।",
    "helpers.role_service_type": "भूमिका / सेवा प्रकार",
    "helpers.phone": "फोन",
    "helpers.notes": "नोट्स",
    "helpers.adding": "जोड़ा जा रहा है…",
    "helpers.call": "कॉल",
    "helpers.capacity": "क्षमता",
    "helpers.schedule": "शेड्यूल",
    "helpers.time_off": "छुट्टी",
    "helpers.hours_per_day": "प्रतिदिन घंटे",
    "helpers.hours_per_day_suffix": "घं/दिन",
    "helpers.helper_default_type": "हेल्पर",
    "helpers.helper_schedule": "हेल्पर शेड्यूल",
    "helpers.helper_capacity": "हेल्पर क्षमता",
    "helpers.helper_time_off": "हेल्पर छुट्टी",
    "helpers.add_time_off": "छुट्टी जोड़ें",
    "helpers.recent_time_off": "हाल की छुट्टी",
    "helpers.no_time_off": "कोई छुट्टी नहीं जोड़ी गई।",
    "helpers.on_leave": "छुट्टी पर",
    "helpers.helper_on_leave": "सहायक छुट्टी पर है।",
    "helpers.reason": "कारण",
    "helpers.feedback": "प्रतिक्रिया",
    "helpers.view_feedback": "प्रतिक्रिया देखें",
    "helpers.rating": "रेटिंग",
    "helpers.comment_optional": "टिप्पणी (वैकल्पिक)",
    "helpers.submit_feedback": "प्रतिक्रिया सबमिट करें",
    "helpers.recent_feedback": "हाल की प्रतिक्रिया",
    "helpers.no_feedback": "कोई प्रतिक्रिया नहीं।",
    "helpers.feedback_submitted": "प्रतिक्रिया सबमिट हो गई।",
    "helpers.rewards": "पुरस्कार",
    "helpers.add_reward": "पुरस्कार जोड़ें",
    "helpers.recent_rewards": "हाल के पुरस्कार",
    "helpers.no_rewards": "कोई पुरस्कार नहीं।",
    "helpers.quarter": "तिमाही",
    "helpers.reward_type": "पुरस्कार प्रकार",
    "helpers.amount_optional": "राशि (वैकल्पिक)",
    "helpers.currency_optional": "मुद्रा (वैकल्पिक)",
    "helpers.reward_reason_optional": "कारण (वैकल्पिक)",
    "helpers.submit_reward": "पुरस्कार बनाएं",
    "helpers.reward_created": "पुरस्कार बन गया।",
    "helpers.no_days": "कोई दिन नहीं",
    "helpers.enter_valid_times": "मान्य प्रारंभ/अंत समय (HH:MM) दर्ज करें।",
    "helpers.enter_valid_hours": "मान्य घंटे/दिन (0-24) दर्ज करें।",
    "helpers.helper_schedule_updated": "हेल्पर शेड्यूल अपडेट हो गया।",
    "helpers.helper_capacity_updated": "हेल्पर क्षमता अपडेट हो गई।",
    "helpers.helper_created": "हेल्पर बन गया।",
    "helpers.start_end_required": "प्रारंभ और अंत आवश्यक हैं।",
    "helpers.time_off_added": "छुट्टी जोड़ी गई।",
    "helpers.category.all": "सभी",
    "helpers.category.cleaning": "सफाई",
    "helpers.category.maintenance": "रखरखाव",
    "helpers.category.outdoor": "बाहरी",
    "helpers.category.childcare": "बच्चों की देखभाल",
    "helpers.category.technology": "प्रौद्योगिकी",
    "helpers.more_actions": "अधिक क्रियाएं",
    "helpers.delete_helper": "हेल्पर हटाएं",
    "helpers.delete_helper_title": "हेल्पर हटाएं",
    "helpers.delete_helper_confirm": "क्या आप इस हेल्पर को हटाना चाहते हैं?",
    "helpers.helper_deleted": "हेल्पर हटा दिया गया।",
    "helpers.language": "भाषा",
    "helpers.language_short": "भाषा",
    "helpers.set_language": "भाषा सेट करें",
    "helpers.set_language_title": "हेल्पर भाषा सेट करें",
    "helpers.language_updated": "हेल्पर भाषा अपडेट हो गई।",
    "helpers.lang.en": "English",
    "helpers.lang.hi": "हिंदी",
    "helpers.lang.kn": "ಕನ್ನಡ",

    "home_profile.dialog_title": "घर की प्रोफ़ाइल",
    "home_profile.step_home_type": "घर का प्रकार",
    "home_profile.step_rooms": "कमरे",
    "home_profile.step_household": "घर की जानकारी",
    "home_profile.step_review": "समीक्षा",
    "home_profile.view_saved": "आपके घर की सहेजी गई जानकारी।",
    "home_profile.pick_type": "अपना घर का प्रकार चुनें — कमरे और मंज़िलें पहले से भर जाएंगी, जिन्हें आप अगले चरण में बदल सकते हैं।",
    "home_profile.rooms_hint": "कमरों के नाम बदलें या मंज़िल चुनें। डिस्प्ले नाम वही होंगे जो हेल्पर्स और असिस्टेंट देखेंगे।",
    "home_profile.household_hint": "घर की जानकारी — शेड्यूल और सुझावों को व्यक्तिगत बनाने में मदद करती है।",
    "home_profile.review_hint": "अपनी घर की प्रोफ़ाइल की समीक्षा करें। कुछ भी बदलने के लिए वापस जाएं।",
    "home_profile.field_area": "कुल क्षेत्रफल (वर्ग फुट)",
    "home_profile.field_floors": "मंज़िलों की संख्या",
    "home_profile.field_floor": "मंज़िल",
    "home_profile.field_display_name": "प्रदर्शन नाम",
    "home_profile.field_template_name": "टेम्पलेट नाम",
    "home_profile.field_bathrooms": "बाथरूम",
    "home_profile.field_flooring": "फ़र्श का प्रकार (वैकल्पिक)",
    "home_profile.field_flooring_placeholder": "जैसे संगमरमर, टाइल, लकड़ी",
    "home_profile.add_room": "कमरा जोड़ें",
    "home_profile.add_room_placeholder": "नाम टाइप करें या सुझाव चुनें",
    "home_profile.room_name": "कमरे का नाम",
    "home_profile.rename_room": "कमरे का नाम बदलें",
    "home_profile.no_rooms_yet": "अभी तक कोई कमरा नहीं जोड़ा गया। नीचे फ़ॉर्म का उपयोग करें।",
    "home_profile.add": "जोड़ें",
    "home_profile.label_type": "प्रकार",
    "home_profile.label_area": "क्षेत्रफल",
    "home_profile.label_sq_ft": "वर्ग फुट",
    "home_profile.label_floors": "मंज़िलें",
    "home_profile.label_bathrooms": "बाथरूम",
    "home_profile.label_pets": "पालतू जानवर",
    "home_profile.label_kids": "बच्चे",
    "home_profile.label_flooring": "फ़र्श",
    "home_profile.label_yes": "हाँ",
    "home_profile.label_no": "नहीं",
    "home_profile.label_bhk": "BHK",
    "home_profile.floor_ground": "भूतल",
    "home_profile.floor_unassigned": "अनिर्दिष्ट",
    "home_profile.sq_ft_up_to": "< {max} वर्ग फुट",
    "home_profile.sq_ft_from": "{min}+ वर्ग फुट",
    "home_profile.sq_ft_range": "{min}–{max} वर्ग फुट",
    "home_profile.rooms_count": "{n} कमरे · {floors} मंज़िल",
    "home_profile.rooms_count_plural": "{n} कमरे · {floors} मंज़िलें",
    "home_profile.pets_label": "पालतू जानवर",
    "home_profile.kids_label": "बच्चे",
    "home_profile.discard": "रद्द करें",
    "home_profile.back": "वापस",
    "home_profile.next": "आगे",
    "home_profile.save": "सहेजें",
    "home_profile.edit": "संपादित करें",
    "home_profile.close": "बंद करें",
    "home_profile.subtitle": "अपने घर के कमरे, लेआउट, और घरेलू विवरण प्रबंधित करें।",
    "home_profile.empty_title": "अभी तक कोई होम प्रोफ़ाइल नहीं",
    "home_profile.create_button": "होम प्रोफ़ाइल बनाएं",
    "home_profile.draft_found": "पिछले सत्र से असहेजे परिवर्तन हैं। उन्हें पुनर्स्थापित करें या रद्द करें?",
    "home_profile.draft_restore": "पुनर्स्थापित करें",
    "home_profile.draft_discard": "रद्द करें",
    "home_profile.draft_restored": "असहेजे परिवर्तन पुनर्स्थापित किए गए। संपादन जारी रखें और सहेजें पर क्लिक करें।",

    "onboarding.welcome_title": "HomeOps में आपका स्वागत है",
    "onboarding.welcome_subtitle": "आपका AI-संचालित घर प्रबंधन सहायक",
    "onboarding.welcome_bullet_1": "कामकाज, हेल्पर्स और शेड्यूल को आसानी से प्रबंधित करें",
    "onboarding.welcome_bullet_2": "AI एजेंट स्वचालित रूप से समन्वय और समायोजन करता है",
    "onboarding.welcome_bullet_3": "अंग्रेज़ी, हिंदी और कन्नड़ में उपलब्ध",
    "onboarding.get_started": "शुरू करें",
    "onboarding.step_welcome": "स्वागत",
    "onboarding.step_profile": "आपकी प्रोफ़ाइल",
    "onboarding.step_home": "आपका घर",
    "onboarding.step_agent": "एजेंट से मिलें",
    "onboarding.profile_name_title": "अपने बारे में बताएं",
    "onboarding.profile_name_label": "आपका नाम",
    "onboarding.role_title": "घर में आपकी भूमिका क्या है?",
    "onboarding.role_subtitle": "यह एजेंट को यह समझने में मदद करता है कि कौन क्या प्रबंधित करता है।",
    "onboarding.role_primary_manager": "प्राथमिक प्रबंधक",
    "onboarding.role_primary_manager_desc": "मैं दिन-प्रतिदिन घर चलाता/चलाती हूं",
    "onboarding.role_shared_responsibility": "साझा ज़िम्मेदारी",
    "onboarding.role_shared_responsibility_desc": "हम घरेलू काम बराबर बांटते हैं",
    "onboarding.role_contributor": "योगदानकर्ता",
    "onboarding.role_contributor_desc": "मैं विशिष्ट कामों में मदद करता/करती हूं",
    "onboarding.role_observer": "दर्शक",
    "onboarding.role_observer_desc": "मैं ज़्यादातर देखता/देखती हूं कि क्या हो रहा है",
    "onboarding.goals_title": "आपके लिए सबसे ज़्यादा क्या मायने रखता है?",
    "onboarding.goals_subtitle": "एक या अधिक प्राथमिकताएं चुनें। एजेंट सुझावों को उसी के अनुसार ढालेगा।",
    "onboarding.goal_cleanliness": "स्वच्छता",
    "onboarding.goal_health_nutrition": "स्वास्थ्य और पोषण",
    "onboarding.goal_cost_optimization": "लागत अनुकूलन",
    "onboarding.goal_time_saving": "समय बचत",
    "onboarding.language_title": "पसंदीदा भाषा",
    "onboarding.language_subtitle": "वह भाषा चुनें जिसमें आप सहायक से बात करना चाहते हैं।",
    "onboarding.schedule_title": "आपकी उपलब्धता",
    "onboarding.schedule_subtitle": "आप आमतौर पर कब खाली होते हैं? यह वैकल्पिक है — एजेंट को आपके शेड्यूल के हिसाब से योजना बनाने में मदद करता है।",
    "onboarding.schedule_hours": "कार्य समय",
    "onboarding.schedule_hours_help": "जैसे 9-18 का मतलब सुबह 9 से शाम 6 बजे",
    "onboarding.schedule_skip": "यह चरण छोड़ें",
    "onboarding.agent_title": "अपने घर के सहायक से मिलें",
    "onboarding.agent_subtitle": "आपका AI एजेंट इन सब और बहुत कुछ में मदद कर सकता है:",
    "onboarding.agent_example_1": "\"सभी कमरों की रोज़ सफ़ाई सेट करें\"",
    "onboarding.agent_example_2": "\"कुक को वीकडेज़ पर लंच बनाने का काम दें\"",
    "onboarding.agent_example_3": "\"मेरी हेल्पर अगले हफ़्ते छुट्टी पर है — शेड्यूल बदलें\"",
    "onboarding.agent_example_4": "\"आज कौन से काम बाकी हैं?\"",
    "onboarding.finish": "HomeOps का उपयोग शुरू करें",
    "onboarding.skip_all": "अभी छोड़ें",
    "onboarding.name_hint": "यह आपके एजेंट को अनुभव को व्यक्तिगत बनाने में मदद करता है।",
    "onboarding.your_name": "आपका नाम",
    "onboarding.continue_to_agent": "जारी रखें — अपने एजेंट से मिलें",
    "onboarding.confirm_selection": "चयन की पुष्टि करें",
    "onboarding.confirm_rooms": "कमरों की पुष्टि करें",
    "onboarding.select_features": "आपके घर में क्या है?",
    "onboarding.confirm_features": "सुविधाओं की पुष्टि करें",
    "onboarding.confirm_details": "विवरण की पुष्टि करें",
    "onboarding.review_chores": "सुझाए गए कार्यों की समीक्षा करें",
    "onboarding.create_chores": "कार्य बनाएं",
    "onboarding.review_chores_hint": "शीर्षक और आवृत्ति संपादित करें, अनावश्यक कार्यों को अनचेक करें।",
    "onboarding.skip_chores": "अभी छोड़ें",
    "onboarding.add_helper": "अपने घरेलू हेल्पर जोड़ें",
    "onboarding.add_helpers_hint": "सभी हेल्पर जोड़ें, फिर एक साथ सेव करें।",
    "onboarding.add_to_list": "सूची में जोड़ें",
    "onboarding.save_helpers": "हेल्पर सेव करें",
    "onboarding.skip_helpers": "कोई हेल्पर नहीं / छोड़ें",
    "onboarding.setup_progress": "सेटअप प्रगति",
    "onboarding.skip_step": "यह चरण छोड़ें",
    "onboarding.finish_later": "बाद में पूरा करें",
    "onboarding.setup_complete": "सेटअप पूर्ण!",
    "onboarding.setup_complete_hint": "आपका घर सेट अप हो गया है। आप साइडबार से कभी भी बदलाव कर सकते हैं।",
    "onboarding.assign_chores_now": "हेल्परों को कार्य सौंपें",
    "onboarding.assign_later": "बाद में करूंगा",

    "nav.dashboard": "डैशबोर्ड",
    "nav.home_profile": "घर की प्रोफ़ाइल",
    "nav.chores": "कार्य",
    "nav.coverage": "कवरेज",
    "nav.events": "घटनाएं",
    "nav.recipes": "व्यंजन विधियां",
    "nav.helpers": "सहायक",
    "nav.services": "सेवाएं",
    "nav.maintenance": "रखरखाव",
    "nav.alerts": "सूचनाएं",
    "nav.automations": "स्वचालन",
    "nav.signals": "सिग्नल",
    "nav.chat": "चैट सहायक",
    "nav.status": "कार्य स्थिति",
    "nav.tests": "परीक्षण",
    "nav.admin": "व्यवस्थापक कॉन्फ़िगरेशन",
    "nav.analytics": "विश्लेषण",
    "nav.support": "सहायता पैनल",

    "coverage.title": "कवरेज",
    "coverage.subtitle": "देखें कि क्या हेल्पर्स और डिवाइस से कवर हो रहा है, और अंतराल कहां हैं",
    "coverage.map_title": "कवरेज मैप",
    "coverage.map_subtitle": "स्थान बनाम आवृत्ति — कवर करने वाले के अनुसार रंगीन",
    "coverage.gaps_title": "कवरेज अंतराल",
    "coverage.gaps_subtitle": "ऐसे स्थान जहां न तो कोई कार्य है और न ही ऑटोमेशन",
    "coverage.empty_title": "अभी तक कोई कवरेज डेटा नहीं",
    "coverage.empty_subtitle": "कवरेज प्लानर का उपयोग करके अपने घर के प्रत्येक स्थान के बारे में बताएं और एक व्यक्तिगत काम सूची प्राप्त करें।",
    "coverage.first_time_title": "अपने प्रारंभिक काम सेट करें",
    "coverage.first_time_subtitle": "हमने आपके होम प्रोफ़ाइल में {count} स्थान पाए। प्रारंभिक काम सूची बनाने के लिए प्लानर का उपयोग करें — आप बाद में बदल सकते हैं।",
    "coverage.audit_button": "कवरेज ऑडिट",
    "coverage.audit_view": "कवरेज ऑडिट",
    "coverage.matrix_view": "पूर्ण मैट्रिक्स",
    "coverage.bulk_add_button": "थोक में जोड़ें",

    "coverage_audit.title": "कवरेज ऑडिट",
    "coverage_audit.subtitle": "देखें क्या गायब है और केवल ज़रूरत की चीज़ें जोड़ें",
    "coverage_audit.gaps_section": "ठीक करने के लिए अंतराल",
    "coverage_audit.full_map_title": "पूरा कवरेज मैप दिखाएं",
    "coverage_audit.no_spaces": "आपके होम प्रोफ़ाइल में अभी तक कोई स्थान नहीं है। कवरेज ऑडिट शुरू करने के लिए पहले होम प्रोफ़ाइल सेट करें।",

    "health.title": "आपके घर का कवरेज",
    "health.score_label": "स्कोर",
    "health.summary": "{total} कमरे — {covered} पूरी तरह कवर्ड, {partial} आंशिक, {gaps} में अंतराल",
    "health.fully_covered": "पूरी तरह कवर्ड",
    "health.partial": "आंशिक",
    "health.gaps": "अंतराल",
    "health.fix_all": "{count} अंतराल ठीक करें",
    "health.critical_gaps_label": "{count} गंभीर",

    "gaps.severity_critical": "गंभीर",
    "gaps.severity_important": "महत्वपूर्ण",
    "gaps.severity_nice": "अच्छा होगा",
    "gaps.critical_intro": "ये अंतराल दैनिक स्वच्छता और सुरक्षा को प्रभावित करते हैं। पहले इन्हें ठीक करना अनुशंसित है।",
    "gaps.empty_title": "सब कवर्ड!",
    "gaps.empty_subtitle": "हर अनुशंसित काम मौजूद है। आपका घर अच्छी तरह कवर्ड है।",
    "gaps.chore_title": "काम का शीर्षक",
    "gaps.add_chore": "काम जोड़ें",
    "gaps.dismiss": "ज़रूरत नहीं",
    "gaps.added_success": "\"{title}\" आपके कामों में जोड़ा गया।",
    "gaps.dismissed_success": "ठीक है — हम फिर से इसका सुझाव नहीं देंगे।",
    "gaps.fix_all_success": "{count} काम बनाए गए।",
    "gaps.fix_all_partial": "{added} काम जोड़े गए, {failed} विफल रहे।",

    "engine.sync_schedule": "शेड्यूल सिंक",
    "engine.syncing": "सिंक हो रहा है…",
    "engine.sync_results": "शेड्यूल सिंक",
    "engine.sync_summary": "{scheduled} नए काम शेड्यूल करने हैं, {adjusted} समायोजन अनुशंसित",
    "engine.section_scheduled": "शेड्यूल करने के लिए नए काम",
    "engine.section_adjustments": "समायोजन",
    "engine.apply": "लागू करें",
    "engine.applied": "लागू",
    "engine.apply_all": "सभी लागू करें ({count})",
    "engine.edit_title": "काम का शीर्षक",
    "engine.nothing_to_sync": "सब अपडेट है। कोई बदलाव नहीं चाहिए।",
    "engine.tasks_across_rooms": "{rooms} कमरों में",
    "engine.apply_room": "कमरा लागू करें",
    "engine.escalation_title": "हस्तक्षेप आवश्यक — काम खतरे में",

    "planner.empty_coverage": "अभी तक कोई स्थान नहीं मिला। प्लानर का उपयोग करके अपने स्थान सेट करें और एक व्यक्तिगत काम सूची प्राप्त करें।",

    "events.title": "घरेलू घटनाएं",
    "events.subtitle": "मेहमान, छुट्टियां, अवसर और अन्य घटनाएं ट्रैक करें",
    "events.add_event": "घटना जोड़ें",
    "events.empty": "अभी तक कोई घटना नहीं। मेहमान, छुट्टी या विशेष अवसर जोड़ें।",
    "events.type": "प्रकार",
    "events.start_at": "शुरू दिनांक/समय",
    "events.end_at": "समाप्ति दिनांक/समय",
    "events.end_at_optional": "वैकल्पिक",
    "events.notes": "नोट्स",
    "events.type_guest_arrival": "मेहमान आगमन",
    "events.type_vacation": "छुट्टी",
    "events.type_occasion": "विशेष अवसर",
    "events.type_weather": "मौसम घटना",
    "events.type_member_health": "सदस्य स्वास्थ्य",
    "events.type_helper_leave": "हेल्पर अवकाश",
    "events.replan_hint": "घटना सहेजी गई — कार्य सुझाव बदल सकते हैं",

    "helpers.workload_title": "हेल्पर का काम का बोझ",
    "helpers.workload_subtitle": "आज के असाइनमेंट और क्षमता उपयोग",
    "helpers.overdue": "देर से",
    "helpers.no_capacity_set": "दैनिक क्षमता सेट नहीं है",
    "helpers.over_capacity": "क्षमता से अधिक — पुनर्संतुलन पर विचार करें",

    "chores.bulk_reassign_title": "थोक में काम पुन: असाइन करें",
    "chores.bulk_reassign_subtitle": "वर्तमान में {name} को सौंपे गए कामों को पुन: असाइन करें",
    "chores.reassign_to": "पुन: असाइन करें",
    "chores.no_chores_to_reassign": "पुन: असाइन करने के लिए कोई सक्रिय काम नहीं",
    "chores.selected_count": "{count} में से {total} चयनित",
    "chores.bulk_reassign_apply": "{count} पुन: असाइन करें",

    "chores.title": "काम",
    "chores.subtitle": "घर के कामों और असाइनमेंट का प्रबंधन करें",
    "chores.back_to_coverage": "कवरेज पर वापस जाएं",
    "chores.view_all": "सभी काम देखें",
    "chores.daily_chores": "दैनिक काम",
    "chores.add_chore": "काम जोड़ें",
    "chores.remove_duplicates": "नकल हटाएं",
    "chores.coverage": "कवरेज",
    "chores.planned_frequency": "स्थान के अनुसार नियोजित आवृत्ति",
    "chores.edit_baseline": "बेसलाइन संपादित करें",
    "chores.autofill_missing": "गायब कवरेज ऑटो-भरें",
    "chores.no_chores_yet": "अभी तक कोई काम नहीं।",
    "chores.daily": "दैनिक",
    "chores.weekly": "साप्ताहिक",
    "chores.biweekly": "द्वि-साप्ताहिक",
    "chores.monthly": "मासिक",
    "chores.assign_to": "असाइन करें",
    "chores.unassigned": "असाइन नहीं",
    "chores.current": "वर्तमान",
    "chores.due": "बकाया",
    "chores.no_duplicates_found": "कोई नकल नहीं मिली।",
    "chores.no_localization_needed": "किसी काम को लोकलाइज़ेशन की आवश्यकता नहीं।",
    "chores.localized": "लोकलाइज़ किया गया",
    "chores.chore_s": "काम।",
    "chores.removed": "हटाया गया",
    "chores.duplicate_chore_s": "नकली काम।",
    "chores.assigned_to": "को सौंपा गया",
    "chores.reported_makeup_created": "रिपोर्ट किया गया। मेक-अप काम बनाया गया।",
    "chores.baseline_updated": "बेसलाइन अपडेट हो गई।",
    "chores.missing_baseline_draft": "बेसलाइन ड्राफ्ट गायब है।",
    "chores.no_home_profile": "कोई होम प्रोफ़ाइल नहीं मिली। पहले अपना होम प्रोफ़ाइल बनाएं (Chat → Home Profile), फिर पुनः प्रयास करें।",
    "chores.aria_report_not_done": "नहीं किया गया रिपोर्ट करें",
    "chores.aria_edit_chore": "काम संपादित करें",
    "chores.date": "तारीख",
    "chores.time_zone": "समय क्षेत्र",
    "chores.only_daily_cadence": "केवल दैनिक केडेंस",
    "chores.daily_counts": "दैनिक:",
    "chores.inferred_daily_counts": "अनुमानित दैनिक:",
    "chores.no_chores_found": "कोई काम नहीं मिला",
    "chores.no_chores_due_on_date": "इस तारीख को कोई काम बकाया नहीं।",
    "chores.filters": "फिल्टर",
    "chores.caught_up": "आप सब निपटा चुके हैं!",
    "chores.create_new": "नया काम बनाएं",
    "chores.chore_title": "काम का शीर्षक",
    "chores.description": "विवरण",
    "chores.category": "श्रेणी",
    "chores.due_date": "बकाया तारीख",
    "chores.create_chore": "काम बनाएं",
    "chores.chore_updated": "काम अपडेट किया गया।",
    "chores.no_recommendations_selected": "कोई सिफ़ारिशें चयनित नहीं।",
    "chores.created": "बनाया गया",
    "chores.makeup_already_exists": "इस काम के लिए एक मेक-अप काम पहले से मौजूद है।",
    "chores.edit_coverage_baseline": "कवरेज बेसलाइन संपादित करें",
    "chores.edit_baseline_help": "प्रति स्थान प्रकार के लिए लक्ष्य केडेंस चुनें। कवरेज हीटमैप गायब लक्ष्य कामों को हाइलाइट करता है।",
    "chores.save_baseline": "बेसलाइन सहेजें",
    "chores.autofill_help": "मुख्य स्थानों (रसोई, बाथरूम, बालकनी, छत, गैरेज आदि) के लिए कवरेज सुधारने हेतु अनुशंसित कामों की समीक्षा करें।",
    "chores.prepared": "तैयार",
    "chores.recommendation_s": "सिफ़ारिशें।",
    "chores.no_missing_coverage": "कोई गायब कवरेज नहीं मिली।",
    "chores.create_chores": "काम बनाएं",
    "chores.localize_title": "काम के शब्दों को लोकलाइज़ करें",
    "chores.localize_help": "यह काम के शीर्षक/विवरण को भारतीय संदर्भ के अनुसार अपडेट करता है (उदा., \"vacuum\" → \"sweep and mop\")।",
    "chores.this_will_update": "यह अपडेट करेगा",
    "chores.no_chores_need_localization": "किसी काम को लोकलाइज़ेशन की आवश्यकता नहीं।",
    "chores.update_chores": "काम अपडेट करें",
    "chores.edit_chore": "काम संपादित करें",
    "chores.title_label": "शीर्षक",
    "chores.status": "स्थिति",
    "chores.status_pending": "pending",
    "chores.status_in_progress": "in-progress",
    "chores.status_completed": "completed",
    "chores.priority": "प्राथमिकता (1-3)",
    "chores.space": "स्थान",
    "chores.subspace": "उप-स्थान",
    "chores.cadence": "केडेंस",
    "chores.none": "(कोई नहीं)",
    "chores.helper": "हेल्पर",
    "chores.remove_duplicate_chores": "नकली काम हटाएं",
    "chores.duplicate_rule": "नकल नियम: समान शीर्षक + स्थान + केडेंस।",
    "chores.keep_strategy": "रणनीति: यदि हो तो हाल ही में पूर्ण किया गया रखें, अन्यथा सबसे हाल का रखें।",
    "chores.permanently_delete_prefix": "यह स्थायी रूप से हटा देगा",
    "chores.across": "कुल",
    "chores.duplicate_group_s": "नकली समूह।",
    "chores.delete_duplicates": "नकल हटाएं",
    "chores.helper_view": "हेल्पर व्यू",
    "chores.task_view": "कार्य व्यू",
    "chores.report_not_done": "पूरा नहीं हुआ रिपोर्ट करें",
    "chores.priority_low": "P1 — कम",
    "chores.priority_medium": "P2 — मध्यम",
    "chores.priority_high": "P3 — उच्च",
    "chores.n_selected": "{count} चयनित",
    "chores.select_all": "सभी चुनें",
    "chores.delete_selected": "चयनित हटाएं",
    "chores.confirm_delete": "\"{title}\" हटाएं? यह पूर्ववत नहीं किया जा सकता।",
    "chores.confirm_bulk_delete_title": "{count} कार्य हटाएं?",
    "chores.confirm_bulk_delete_message": "यह चयनित कार्यों को स्थायी रूप से हटा देगा।",
    "chores.choose_space": "स्थान चुनें",
    "chores.choose_space_error": "कृपया एक स्थान चुनें।",
    "chores.apply": "लागू करें",
    "chores.clear_filters": "फ़िल्टर हटाएं",
    "chores.tab_all": "सभी",
    "chores.tab_pending": "लंबित",
    "chores.tab_in_progress": "प्रगति में",
    "chores.tab_completed": "पूर्ण",
    "chores.empty_title": "कोई कार्य नहीं मिला",
    "chores.empty_subtitle": "कार्य जोड़ें या शेड्यूलर चलाएं।",
    "chores.on_leave_warning": "इस तारीख के लिए हेल्पर छुट्टी पर है",

    "replan.title": "स्मार्ट सुझाव",
    "replan.subtitle": "घटनाओं, कवरेज और काम के बोझ के आधार पर {count} सुझाव",
    "replan.button": "सुझाव",
    "replan.empty": "अभी कोई सुझाव नहीं। शुरू करने के लिए घरेलू घटनाएं जोड़ें या कवरेज जांचें।",
    "replan.dismiss": "खारिज करें",
    "replan.severity_info": "जानकारी",
    "replan.severity_warning": "चेतावनी",
    "replan.severity_critical": "गंभीर",
    "replan.kind_reassign_helper": "पुन: असाइन",
    "replan.kind_add_chore": "काम जोड़ें",
    "replan.kind_skip_chore": "छोड़ें",
    "replan.kind_balance_workload": "पुनर्संतुलन",
    "replan.kind_coverage_gap": "कवरेज",

    "common.cancel": "रद्द करें",
    "common.close": "बंद करें",
    "common.save": "सहेजें",
    "common.done": "पूर्ण",
    "common.add": "जोड़ें",
    "common.edit": "संपादित करें",
    "common.delete": "हटाएं",
    "common.search": "खोजें",
    "common.clear": "साफ़ करें",
    "common.start": "शुरू करें",
    "common.end": "समाप्त",
    "common.create_failed": "बनाना विफल",
    "common.update_failed": "अपडेट विफल",
    "common.delete_failed": "हटाना विफल",
    "common.missing_session": "सत्र टोकन या हाउसहोल्ड आईडी अनुपलब्ध है।",
    "common.unknown": "अज्ञात",
    "common.unknown_error": "अज्ञात त्रुटि",
    "weekday.mon": "सोम",
    "weekday.tue": "मंगल",
    "weekday.wed": "बुध",
    "weekday.thu": "गुरु",
    "weekday.fri": "शुक्र",
    "weekday.sat": "शनि",
    "weekday.sun": "रवि",
    "common.loading": "लोड हो रहा है…",
    "common.or": "या",
    "common.logout": "लॉग आउट",
    "common.refresh": "रीफ़्रेश",
    "layout.household_manager": "हाउसहोल्ड मैनेजर",
    "layout.role_demo": "भूमिका (डेमो)",
    "layout.role.household": "हाउसहोल्ड उपयोगकर्ता",
    "layout.role.admin": "एडमिन",
    "layout.role.owner": "मालिक / डेव",
    "layout.role.support": "सहायता स्टाफ़",
    "layout.agent_setup": "एजेंट सेटअप",
    "layout.profile": "प्रोफ़ाइल",
    "layout.send_feedback": "प्रतिक्रिया भेजें",
    "layout.chat": "चैट",
    "layout.chat_assistant": "चैट सहायक",
    "signals.title": "सिग्नल्स",
    "signals.subtitle": "चैट और ऑटोमेशन से कैप्चर किए गए घरेलू इवेंट और प्रतिक्रिया",
    "signals.upcoming_events": "आगामी इवेंट",
    "signals.no_upcoming_events": "कोई आगामी इवेंट नहीं",
    "signals.cleaning_feedback": "सफाई प्रतिक्रिया",
    "signals.no_cleaning_feedback": "अभी कोई सफाई प्रतिक्रिया नहीं",
    "signals.rating": "रेटिंग",
    "automations.title": "ऑटोमेशन",
    "automations.subtitle": "शेड्यूल किए गए रिमाइंडर और सक्रिय अलर्ट प्रबंधित करें",
    "automations.summary": "सक्रिय {active} • रोकें {paused} • अक्षम {disabled}",
    "automations.none_title": "अभी कोई ऑटोमेशन नहीं",
    "automations.none_body": "शेड्यूल अलर्ट बनाने के लिए चैट से ऑटोमेशन सुझाव स्वीकृत करें।",
    "automations.cadence": "आवृत्ति",
    "automations.next_run": "अगला रन",
    "automations.last_run": "अंतिम रन",
    "automations.run_now": "अभी चलाएं",
    "automations.pause": "रोकें",
    "automations.resume": "फिर से शुरू करें",
    "automations.disable": "अक्षम करें",
    "automations.edit": "ऑटोमेशन संपादित करें",
    "automations.field_title": "शीर्षक",
    "automations.field_description": "विवरण",
    "automations.field_cadence": "आवृत्ति",
    "automations.field_at_time": "समय पर (वैकल्पिक)",
    "automations.field_day_of_week": "सप्ताह का दिन (साप्ताहिक)",
    "automations.field_day_of_month": "महीने का दिन (मासिक)",
    "automations.cadence_daily": "दैनिक",
    "automations.cadence_weekdays": "कार्यदिवस",
    "automations.cadence_weekly": "साप्ताहिक",
    "automations.cadence_monthly": "मासिक",
    "automations.cadence_hourly": "प्रति घंटा",
    "automations.cadence_every_2_hours": "हर 2 घंटे",
    "automations.cadence_every_5_minutes": "हर 5 मिनट",
    "automations.status_active": "सक्रिय",
    "automations.status_paused": "रोका गया",
    "automations.status_disabled": "अक्षम",
    "automations.title_required": "शीर्षक आवश्यक है",
    "automations.weekly_requires_dow": "साप्ताहिक आवृत्ति के लिए day_of_week आवश्यक है",
    "automations.monthly_requires_dom": "मासिक आवृत्ति के लिए day_of_month आवश्यक है",
    "auth.login.welcome_back": "वापसी पर स्वागत है",
    "auth.login.subtitle": "अपने Home Operations Manager में साइन इन करें",
    "auth.login.email": "ईमेल",
    "auth.login.password": "पासवर्ड",
    "auth.login.remember_me": "मुझे याद रखें",
    "auth.login.forgot_password": "पासवर्ड भूल गए?",
    "auth.login.sign_in": "साइन इन करें",
    "auth.login.google": "Google",
    "auth.login.microsoft": "Microsoft",
    "auth.login.no_account": "खाता नहीं है?",
    "auth.login.sign_up": "साइन अप करें",
    "auth.signup.create_account": "खाता बनाएं",
    "auth.signup.subtitle": "अपने हाउसहोल्ड में शामिल हों या अपना घर प्रबंधित करना शुरू करें",
    "auth.signup.full_name": "पूरा नाम",
    "auth.signup.household_code": "हाउसहोल्ड कोड (वैकल्पिक)",
    "auth.signup.household_code_placeholder": "मौजूदा हाउसहोल्ड कोड दर्ज करें",
    "auth.signup.household_code_help": "नया हाउसहोल्ड बनाने के लिए खाली छोड़ें",
    "auth.signup.confirm_password": "पासवर्ड पुष्टि करें",
    "auth.signup.terms": "मैं सेवा की शर्तों और गोपनीयता नीति से सहमत हूं",
    "auth.signup.create": "खाता बनाएं",
    "auth.signup.have_account": "पहले से खाता है?",
    "auth.signup.sign_in": "साइन इन करें",
    "dashboard.title": "डैशबोर्ड",
    "dashboard.subtitle": "वापसी पर स्वागत है! आपके घर में क्या हो रहा है, यहाँ देखें।",
    "dashboard.setup_incomplete": "आपके घर का सेटअप अधूरा है",
    "dashboard.continue_setup": "सेटअप जारी रखें",
    "dashboard.setup_home_profile": "घर की प्रोफ़ाइल",
    "dashboard.setup_features": "घर की सुविधाएं",
    "dashboard.setup_chores": "कार्य",
    "dashboard.setup_helpers": "सहायक",
    "dashboard.assign_chores_title": "कार्यों को सौंपा जाना बाकी है",
    "dashboard.assign_chores_hint": "{count} कार्य अभी तक किसी हेल्पर को नहीं सौंपे गए हैं।",
    "dashboard.assign_chores_button": "हेल्परों को सौंपें",
    "dashboard.upcoming_chores": "आगामी काम",
    "dashboard.tasks_this_week": "इस सप्ताह के कार्य",
    "dashboard.view_all": "सभी देखें",
    "dashboard.no_upcoming_chores": "कोई आगामी काम नहीं।",
    "dashboard.recent_alerts": "हाल के अलर्ट",
    "dashboard.notifications_and_reminders": "सूचनाएं और रिमाइंडर",
    "dashboard.progress_title": "मार्च की प्रगति",
    "dashboard.progress_subtitle": "इस महीने कार्य पूर्णता",
    "dashboard.completed": "पूर्ण",
    "dashboard.in_progress": "जारी है",
    "dashboard.overdue": "समय सीमा बीत चुकी",
    "dashboard.quick_actions": "त्वरित क्रियाएं",
    "dashboard.add_chore": "काम जोड़ें",
    "dashboard.add_recipe": "रेसिपी जोड़ें",
    "dashboard.chat_assistant": "चैट सहायक",
    "dashboard.view_status": "स्थिति देखें",
    "tests.title": "टेस्ट",
    "tests.subtitle": "अपने स्वचालित टेस्ट सूट चलाएं और शेड्यूल करें",
    "tests.run_now": "अभी चलाएं",
    "tests.run_now_help": "टेस्ट रन शुरू करें और परिणाम व लॉग ट्रैक करें",
    "tests.run_vitest": "Vitest चलाएं",
    "tests.run_vitest_watch": "Vitest (वॉच)",
    "tests.run_vitest_ui": "Vitest UI",
    "tests.run_playwright": "Playwright चलाएं",
    "tests.run_playwright_ui": "Playwright UI",
    "tests.schedules": "शेड्यूल",
    "tests.schedules_help": "क्रॉन शेड्यूल आपके लोकल टेस्ट रनर सर्विस द्वारा निष्पादित किए जाते हैं",
    "tests.schedule_name": "शेड्यूल नाम",
    "tests.cron_expr": "क्रॉन एक्सप्रेशन",
    "tests.type": "प्रकार",
    "tests.mode": "मोड",
    "tests.add_schedule": "शेड्यूल जोड़ें",
    "tests.no_schedules": "अभी कोई शेड्यूल नहीं।",
    "tests.last_run": "अंतिम रन",
    "tests.enable": "सक्षम करें",
    "tests.disable": "अक्षम करें",
    "tests.recent_runs": "हाल के रन",
    "tests.recent_runs_help": "लोकल रनर द्वारा कैप्चर किए गए हाल के टेस्ट रन",
    "tests.no_runs": "अभी कोई रन नहीं।",
    "tests.created": "बनाया गया",
    "tests.started": "शुरू हुआ",
    "tests.finished": "समाप्त हुआ",
    "tests.view_log": "लॉग देखें",
    "tests.view_report": "रिपोर्ट देखें",
    "tests.cancel": "रद्द करें",
    "recipes.title": "रेसिपी",
    "recipes.subtitle": "आपके परिवार के पसंदीदा भोजन और कुकिंग आइडिया",
    "recipes.add_recipe": "रेसिपी जोड़ें",
    "recipes.youtube_search": "YouTube रेसिपी खोज",
    "recipes.youtube_search_subtitle": "कस्टमाइज़ करने योग्य क्वेरी विकल्पों के साथ YouTube पर रेसिपी खोजें",
    "recipes.search_options": "खोज विकल्प",
    "recipes.recipe": "रेसिपी",
    "recipes.generated_query": "जनरेट की गई क्वेरी",
    "recipes.search_youtube": "YouTube पर खोजें",
    "recipes.use_current_search": "वर्तमान पेज खोज का उपयोग करें",
    "recipes.search_placeholder": "नाम या व्यंजन से रेसिपी खोजें...",
    "recipes.category": "श्रेणी",
    "recipes.all": "सभी",
    "recipes.breakfast": "नाश्ता",
    "recipes.lunch": "दोपहर का भोजन",
    "recipes.dinner": "रात का भोजन",
    "recipes.dessert": "मिठाई",
    "recipes.ingredients": "सामग्री:",
    "recipes.more": "और...",
    "recipes.view_full": "पूरी रेसिपी देखें",
    "recipes.no_recipes": "कोई रेसिपी नहीं मिली",
    "recipes.no_recipes_hint": "अपनी खोज समायोजित करने या नई रेसिपी जोड़ने का प्रयास करें",
    "recipes.add_new_recipe": "नई रेसिपी जोड़ें",
    "recipes.recipe_title": "रेसिपी शीर्षक",
    "recipes.description": "विवरण",
    "recipes.cuisine": "व्यंजन",
    "recipes.prep_time": "तैयारी का समय",
    "recipes.cook_time": "पकाने का समय",
    "recipes.servings": "सर्विंग्स",
    "recipes.instructions": "निर्देश",
    "recipes.save_recipe": "रेसिपी सहेजें",
    "recipes.youtube_options": "YouTube खोज विकल्प",
    "recipes.query_template": "क्वेरी टेम्पलेट",
    "recipes.preferred_channels": "पसंदीदा चैनल / कीवर्ड (वैकल्पिक)",
    "recipes.include_shorts": "Shorts शामिल करें",
    "recipes.no": "नहीं",
    "recipes.yes": "हाँ",
    "recipes.tab_recipes": "रेसिपी",
    "recipes.tab_meal_plan": "भोजन योजना",
    "recipes.tab_shopping_list": "खरीदारी सूची",
    "recipes.edit_recipe": "रेसिपी संपादित करें",
    "recipes.source_url": "स्रोत URL",
    "recipes.ingredients_hint": "प्रति पंक्ति एक सामग्री",
    "recipes.prev_week": "पिछला सप्ताह",
    "recipes.next_week": "अगला सप्ताह",
    "recipes.assign_meal": "भोजन की योजना बनाएं",
    "recipes.date": "दिनांक",
    "recipes.meal_type": "भोजन",
    "recipes.pick_recipe": "रेसिपी चुनें",
    "recipes.or": "या",
    "recipes.custom_meal": "कस्टम भोजन",
    "recipes.notes": "नोट्स",
    "recipes.shopping_list_hint": "इस सप्ताह की भोजन योजना के लिए आवश्यक सामग्री, आपकी पैंट्री से तुलना",
    "recipes.shopping_list_empty": "अभी कोई आइटम नहीं। खरीदारी सूची बनाने के लिए सप्ताह के भोजन की योजना बनाएं।",
    "alerts.no_alerts": "कोई अलर्ट नहीं",
    "alerts.caught_up": "आप पूरी तरह अपडेटेड हैं!",
    "alerts.title": "अलर्ट और सूचनाएं",
    "alerts.subtitle": "घरेलू गतिविधियों और रिमाइंडर के बारे में सूचित रहें",
    "alerts.unread": "अपठित",
    "alerts.mark_all_read": "सभी को पठित चिह्नित करें",
    "alerts.all": "सभी",
    "alerts.critical_tab": "गंभीर",
    "alerts.warning_tab": "चेतावनी",
    "alerts.info_tab": "जानकारी",
    "alerts.create_custom": "कस्टम अलर्ट बनाएं",
    "alerts.alert_title": "अलर्ट शीर्षक",
    "alerts.message": "संदेश",
    "alerts.priority": "प्राथमिकता",
    "alerts.category": "श्रेणी",
    "alerts.critical": "गंभीर",
    "alerts.warning": "चेतावनी",
    "alerts.info": "जानकारी",
    "alerts.maintenance": "रखरखाव",
    "alerts.bills": "बिल",
    "alerts.inventory": "इन्वेंटरी",
    "alerts.safety": "सुरक्षा",
    "alerts.reminders": "रिमाइंडर",
    "alerts.alert_date_time": "अलर्ट तिथि और समय",
    "alerts.create": "अलर्ट बनाएं",
    "admin.title": "एडमिन कॉन्फ़िगरेशन",
    "admin.subtitle": "हाउसहोल्ड सेटिंग्स और प्राथमिकताएं प्रबंधित करें",
    "admin.general": "सामान्य",
    "admin.members": "सदस्य",
    "admin.notifications": "सूचनाएं",
    "admin.security": "सुरक्षा",
    "admin.integrations": "इंटीग्रेशन",
    "admin.agents": "एजेंट",
    "admin.agents_subtitle": "बिल्ट-इन एजेंट प्रबंधित करें (हाउसहोल्ड ओवरराइड)",
    "admin.agents_empty": "कोई एजेंट नहीं मिला",
    "admin.agent_enabled": "सक्षम",
    "admin.agent_key": "कुंजी",
    "admin.agent_display_name": "प्रदर्शन नाम",
    "admin.agent_model": "मॉडल (वैकल्पिक)",
    "admin.agent_system_prompt": "सिस्टम प्रॉम्प्ट",
    "admin.agent_tool_allowlist": "टूल अनुमति सूची (अल्पविराम से अलग)",
    "admin.agent_tool_allowlist_help": "उदाहरण: db.select, db.insert",
    "admin.members_subtitle": "हाउसहोल्ड सदस्य देखें",
    "admin.members_empty": "कोई सदस्य नहीं मिला",
    "admin.notifications_subtitle": "सूचना प्राथमिकताएं",
    "admin.notifications_enabled": "सूचनाएं सक्षम",
    "admin.email_digest": "ईमेल डाइजेस्ट",
    "admin.security_subtitle": "सुरक्षा प्राथमिकताएं",
    "admin.two_factor_auth": "दो-कारक प्रमाणीकरण",
    "admin.auto_assign_chores": "कामों को स्वतः असाइन करें",
    "admin.integrations_subtitle": "इंटीग्रेशन सेटिंग्स",
    "admin.youtube_settings": "YouTube सेटिंग्स",
    "admin.recipe_settings": "रेसिपी सेटिंग्स",
    "admin.allowed_sources": "अनुमत स्रोत (अल्पविराम से अलग)",
    "admin.min_rating": "न्यूनतम रेटिंग",
    "admin.min_reviews": "न्यूनतम समीक्षाएं",
    "admin.lenient_missing_reviews": "समीक्षा अनुपलब्ध होने पर उदार",
    "admin.invite_email": "आमंत्रण ईमेल",
    "admin.invite_role": "आमंत्रण भूमिका",
    "admin.role_member": "सदस्य",
    "admin.role_admin": "एडमिन",
    "admin.role_owner": "मालिक",
    "admin.send_invite": "आमंत्रण भेजें",
    "admin.pending_invites": "लंबित आमंत्रण",
    "admin.invites_empty": "कोई आमंत्रण नहीं",
    "admin.copy_invite_link": "लिंक कॉपी करें",
    "admin.revoke_invite": "रद्द करें",
    "admin.invite_status_pending": "लंबित",
    "admin.invite_status_accepted": "स्वीकृत",
    "admin.invite_status_revoked": "रद्द किया गया",
    "invite.accept.title": "आमंत्रण स्वीकार करें",
    "invite.accept.subtitle": "हाउसहोल्ड में शामिल हों",
    "invite.accept.missing_token": "आमंत्रण टोकन अनुपलब्ध",
    "invite.accept.working": "आमंत्रण स्वीकार किया जा रहा है…",
    "invite.accept.success": "आमंत्रण स्वीकार किया गया। आप अब हाउसहोल्ड के सदस्य हैं।",
    "invite.accept.go_home": "डैशबोर्ड पर जाएं",
    "admin.household_information": "हाउसहोल्ड जानकारी",
    "admin.household_information_subtitle": "आपके हाउसहोल्ड के बारे में बुनियादी जानकारी",
    "admin.household_name": "हाउसहोल्ड नाम",
    "admin.address": "पता",
    "admin.timezone": "समय क्षेत्र",
    "admin.language": "भाषा",
    "admin.save_changes": "परिवर्तन सहेजें",
    "support.tickets": "सहायता टिकट",
    "support.subheader": "सभी उपयोगकर्ता सहायता अनुरोध",
    "support.title": "सहायता पैनल",
    "support.subtitle": "उपयोगकर्ता सहायता अनुरोधों का प्रबंधन और जवाब दें",
    "support.open_tickets": "खुले टिकट",
    "support.in_progress": "जारी है",
    "support.resolved_today": "आज हल किए गए",
    "support.avg_response_time": "औसत प्रतिक्रिया समय",
    "support.search_placeholder": "टिकट खोजें...",
    "support.all": "सभी",
    "support.open": "खुला",
    "support.resolved": "हल किया गया",
    "support.type_response": "अपनी प्रतिक्रिया टाइप करें...",
    "chat.review_home_profile": "होम प्रोफ़ाइल समीक्षा करें",
    "chat.recommended_chores": "अनुशंसित काम",
    "chat.recommended_chores_help": "जो चाहिए वह चुनें, यदि आवश्यक हो तो ही संपादित करें, फिर सबमिट करें।",
    "chat.recommended_automations": "अनुशंसित ऑटोमेशन",
    "chat.recommended_automations_help": "समीक्षा करें और शेड्यूल अलर्ट में बदलने के लिए स्वीकृत करें।",
    "chat.toggle_all": "सभी टॉगल करें",
    "chat.submit": "सबमिट करें",
    "chat.edit_chore": "काम संपादित करें",
    "chat.chore_title": "शीर्षक",
    "chat.chore_description": "विवरण",
    "chat.frequency": "आवृत्ति",
    "chat.frequency_not_set": "सेट नहीं",
    "chat.frequency_daily": "दैनिक",
    "chat.frequency_weekly": "साप्ताहिक",
    "chat.frequency_biweekly": "द्वि-साप्ताहिक",
    "chat.frequency_monthly": "मासिक",
    "chat.due": "देय",
    "chat.helper": "हेल्पर",
    "chat.unassigned": "असाइन नहीं",
    "chat.approve": "स्वीकृत करें",
    "chat.reject": "अस्वीकार करें",
    "chat.apply": "लागू करें",
    "chat.create_table": "{table} बनाएं",
    "chat.tool_execution_failed": "टूल निष्पादन विफल",
    "chat.executed_tool": "{tool} निष्पादित किया।",
    "chat.create_home_profile_first": "कृपया पहले अपनी होम प्रोफ़ाइल बनाएं, फिर सिफारिशें जनरेट करें।",
    "chat.could_not_fetch": "जानकारी प्राप्त नहीं की जा सकी",
    "chat.transliteration_off": "ABC",
    "helper_daily.empty_hint": "इस तिथि के लिए कोई काम शेड्यूल नहीं है। काम जनरेट करने के लिए सिंक शेड्यूल का उपयोग करें, या सभी काम देखने के लिए लिस्ट व्यू पर स्विच करें।",
  },
  kn: {
    "helpers.title": "ಹೆಲ್ಪರ್‌ಗಳು ಮತ್ತು ಸೇವೆಗಳು",
    "helpers.subtitle": "ಮನೆಯ ಸೇವಾ ಪೂರೈಕೆದಾರರನ್ನು ನಿರ್ವಹಿಸಿ",
    "helpers.add_helper": "ಹೆಲ್ಪರ್ ಸೇರಿಸಿ",
    "helpers.onboard_helper": "ಹೆಲ್ಪರ್ ಆನ್‌ಬೋರ್ಡ್ ಮಾಡಿ",
    "helpers.empty_title": "ಇನ್ನೂ ಯಾವುದೇ ಸಹಾಯಕರಿಲ್ಲ",
    "helpers.empty_subtitle": "ಮನೆಯ ಸಿಬ್ಬಂದಿ ನಿರ್ವಹಣೆ ಪ್ರಾರಂಭಿಸಲು ನಿಮ್ಮ ಮೊದಲ ಸಹಾಯಕರನ್ನು ಸೇರಿಸಿ.",
    "helpers.add_new_helper": "ಹೊಸ ಹೆಲ್ಪರ್ ಸೇರಿಸಿ",
    "helpers.name_business": "ಹೆಸರು / ವ್ಯವಹಾರ ಹೆಸರು",
    "helpers.name_required": "ಹೆಸರು ಅಗತ್ಯ.",
    "helpers.role_service_type": "ಪಾತ್ರ / ಸೇವೆಯ ಪ್ರಕಾರ",
    "helpers.phone": "ಫೋನ್",
    "helpers.notes": "ಟಿಪ್ಪಣಿಗಳು",
    "helpers.adding": "ಸೇರಿಸಲಾಗುತ್ತಿದೆ…",
    "helpers.call": "ಕರೆ",
    "helpers.capacity": "ಸಾಮರ್ಥ್ಯ",
    "helpers.schedule": "ವೇಳಾಪಟ್ಟಿ",
    "helpers.time_off": "ರಜೆ",
    "helpers.hours_per_day": "ಪ್ರತಿ ದಿನ ಗಂಟೆಗಳು",
    "helpers.hours_per_day_suffix": "ಗಂ/ದಿನ",
    "helpers.helper_default_type": "ಹೆಲ್ಪರ್",
    "helpers.helper_schedule": "ಹೆಲ್ಪರ್ ವೇಳಾಪಟ್ಟಿ",
    "helpers.helper_capacity": "ಹೆಲ್ಪರ್ ಸಾಮರ್ಥ್ಯ",
    "helpers.helper_time_off": "ಹೆಲ್ಪರ್ ರಜೆ",
    "helpers.add_time_off": "ರಜೆ ಸೇರಿಸಿ",
    "helpers.recent_time_off": "ಇತ್ತೀಚಿನ ರಜೆ",
    "helpers.no_time_off": "ಯಾವುದೇ ರಜೆ ದಾಖಲಾಗಿಲ್ಲ.",
    "helpers.on_leave": "ರಜೆಯಲ್ಲಿ",
    "helpers.helper_on_leave": "ಹೆಲ್ಪರ್ ರಜೆಯಲ್ಲಿದ್ದಾರೆ.",
    "helpers.reason": "ಕಾರಣ",
    "helpers.feedback": "ಪ್ರತಿಕ್ರಿಯೆ",
    "helpers.view_feedback": "ಪ್ರತಿಕ್ರಿಯೆ ನೋಡಿ",
    "helpers.rating": "ರೇಟಿಂಗ್",
    "helpers.comment_optional": "ಟಿಪ್ಪಣಿ (ಐಚ್ಛಿಕ)",
    "helpers.submit_feedback": "ಪ್ರತಿಕ್ರಿಯೆ ಸಲ್ಲಿಸಿ",
    "helpers.recent_feedback": "ಇತ್ತೀಚಿನ ಪ್ರತಿಕ್ರಿಯೆ",
    "helpers.no_feedback": "ಇನ್ನೂ ಯಾವುದೇ ಪ್ರತಿಕ್ರಿಯೆ ಇಲ್ಲ.",
    "helpers.feedback_submitted": "ಪ್ರತಿಕ್ರಿಯೆ ಸಲ್ಲಿಸಲಾಗಿದೆ.",
    "helpers.rewards": "ಬಹುಮಾನಗಳು",
    "helpers.add_reward": "ಬಹುಮಾನ ಸೇರಿಸಿ",
    "helpers.recent_rewards": "ಇತ್ತೀಚಿನ ಬಹುಮಾನಗಳು",
    "helpers.no_rewards": "ಇನ್ನೂ ಯಾವುದೇ ಬಹುಮಾನಗಳಿಲ್ಲ.",
    "helpers.quarter": "ತ್ರೈಮಾಸಿಕ",
    "helpers.reward_type": "ಬಹುಮಾನ ಪ್ರಕಾರ",
    "helpers.amount_optional": "ಮೊತ್ತ (ಐಚ್ಛಿಕ)",
    "helpers.currency_optional": "ಕರೆನ್ಸಿ (ಐಚ್ಛಿಕ)",
    "helpers.reward_reason_optional": "ಕಾರಣ (ಐಚ್ಛಿಕ)",
    "helpers.submit_reward": "ಬಹುಮಾನ ಸೃಷ್ಟಿಸಿ",
    "helpers.reward_created": "ಬಹುಮಾನ ಸೃಷ್ಟಿಸಲಾಗಿದೆ.",
    "helpers.no_days": "ಯಾವುದೇ ದಿನಗಳು ಇಲ್ಲ",
    "helpers.enter_valid_times": "ಮಾನ್ಯ ಪ್ರಾರಂಭ/ಅಂತ್ಯ ಸಮಯ (HH:MM) ನಮೂದಿಸಿ.",
    "helpers.enter_valid_hours": "ಮಾನ್ಯ ಗಂಟೆ/ದಿನ (0-24) ನಮೂದಿಸಿ.",
    "helpers.helper_schedule_updated": "ಹೆಲ್ಪರ್ ವೇಳಾಪಟ್ಟಿ ನವೀಕರಿಸಲಾಗಿದೆ.",
    "helpers.helper_capacity_updated": "ಹೆಲ್ಪರ್ ಸಾಮರ್ಥ್ಯ ನವೀಕರಿಸಲಾಗಿದೆ.",
    "helpers.helper_created": "ಹೆಲ್ಪರ್ ಸೃಷ್ಟಿಸಲಾಗಿದೆ.",
    "helpers.start_end_required": "ಪ್ರಾರಂಭ ಮತ್ತು ಅಂತ್ಯ ಅಗತ್ಯ.",
    "helpers.time_off_added": "ರಜೆ ಸೇರಿಸಲಾಗಿದೆ.",
    "helpers.category.all": "ಎಲ್ಲಾ",
    "helpers.category.cleaning": "ಸ್ವಚ್ಛತೆ",
    "helpers.category.maintenance": "ನಿರ್ವಹಣೆ",
    "helpers.category.outdoor": "ಹೊರಾಂಗಣ",
    "helpers.category.childcare": "ಮಕ್ಕಳ ಆರೈಕೆ",
    "helpers.category.technology": "ತಂತ್ರಜ್ಞಾನ",
    "helpers.more_actions": "ಹೆಚ್ಚಿನ ಕ್ರಿಯೆಗಳು",
    "helpers.delete_helper": "ಹೆಲ್ಪರ್ ಅಳಿಸಿ",
    "helpers.delete_helper_title": "ಹೆಲ್ಪರ್ ಅಳಿಸಿ",
    "helpers.delete_helper_confirm": "ಈ ಹೆಲ್ಪರ್ ಅನ್ನು ಅಳಿಸಲು ಖಚಿತವೇ?",
    "helpers.helper_deleted": "ಹೆಲ್ಪರ್ ಅಳಿಸಲಾಗಿದೆ.",
    "helpers.language": "ಭಾಷೆ",
    "helpers.language_short": "ಭಾಷೆ",
    "helpers.set_language": "ಭಾಷೆ ಹೊಂದಿಸಿ",
    "helpers.set_language_title": "ಹೆಲ್ಪರ್ ಭಾಷೆ ಹೊಂದಿಸಿ",
    "helpers.language_updated": "ಹೆಲ್ಪರ್ ಭಾಷೆ ನವೀಕರಿಸಲಾಗಿದೆ.",
    "helpers.lang.en": "English",
    "helpers.lang.hi": "हिंदी",
    "helpers.lang.kn": "ಕನ್ನಡ",

    "home_profile.dialog_title": "ಮನೆ ಪ್ರೊಫೈಲ್",
    "home_profile.step_home_type": "ಮನೆ ವಿಧ",
    "home_profile.step_rooms": "ಕೊಠಡಿಗಳು",
    "home_profile.step_household": "ಮನೆ ಮಾಹಿತಿ",
    "home_profile.step_review": "ಪರಿಶೀಲನೆ",
    "home_profile.view_saved": "ನಿಮ್ಮ ಮನೆಯ ಉಳಿಸಿದ ಮಾಹಿತಿ.",
    "home_profile.pick_type": "ನಿಮ್ಮ ಮನೆ ವಿಧ ಆರಿಸಿ — ಕೊಠಡಿಗಳು ಮತ್ತು ಮಹಡಿಗಳನ್ನು ಮೊದಲೇ ತುಂಬಲಾಗುತ್ತದೆ, ಮುಂದಿನ ಹಂತದಲ್ಲಿ ಬದಲಾಯಿಸಬಹುದು.",
    "home_profile.rooms_hint": "ಕೊಠಡಿ ಹೆಸರು ಬದಲಾಯಿಸಿ ಅಥವಾ ಮಹಡಿ ಆರಿಸಿ. ಡಿಸ್‌ಪ್ಲೇ ಹೆಸರುಗಳನ್ನು ಹೆಲ್ಪರ್‌ಗಳು ಮತ್ತು ಅಸಿಸ್ಟೆಂಟ್ ನೋಡುತ್ತಾರೆ.",
    "home_profile.household_hint": "ಮನೆ ಮಾಹಿತಿ — ವೇಳಾಪಟ್ಟಿ ಮತ್ತು ಶಿಫಾರಸುಗಳನ್ನು ವ್ಯಕ್ತಿಗತಗೊಳಿಸಲು ಸಹಾಯ ಮಾಡುತ್ತದೆ.",
    "home_profile.review_hint": "ನಿಮ್ಮ ಮನೆ ಪ್ರೊಫೈಲ್ ಪರಿಶೀಲಿಸಿ. ಏನಾದರೂ ಬದಲಾಯಿಸಲು ಹಿಂದೆ ಹೋಗಿ.",
    "home_profile.field_area": "ಒಟ್ಟು ವಿಸ್ತೀರ್ಣ (ಚದರ ಅಡಿ)",
    "home_profile.field_floors": "ಮಹಡಿಗಳ ಸಂಖ್ಯೆ",
    "home_profile.field_floor": "ಮಹಡಿ",
    "home_profile.field_display_name": "ಪ್ರದರ್ಶನ ಹೆಸರು",
    "home_profile.field_template_name": "ಟೆಂಪ್ಲೇಟ್ ಹೆಸರು",
    "home_profile.field_bathrooms": "ಶೌಚಾಲಯಗಳು",
    "home_profile.field_flooring": "ನೆಲ ಹಾಸಿನ ವಿಧ (ಐಚ್ಛಿಕ)",
    "home_profile.field_flooring_placeholder": "ಉದಾ. ಮಾರ್ಬಲ್, ಟೈಲ್, ಮರ",
    "home_profile.add_room": "ಕೊಠಡಿ ಸೇರಿಸಿ",
    "home_profile.add_room_placeholder": "ಹೆಸರು ಟೈಪ್ ಮಾಡಿ ಅಥವಾ ಸಲಹೆ ಆರಿಸಿ",
    "home_profile.room_name": "ಕೊಠಡಿ ಹೆಸರು",
    "home_profile.rename_room": "ಕೊಠಡಿ ಹೆಸರು ಬದಲಿಸಿ",
    "home_profile.no_rooms_yet": "ಇನ್ನೂ ಯಾವುದೇ ಕೊಠಡಿಗಳನ್ನು ಸೇರಿಸಿಲ್ಲ. ಕೆಳಗಿನ ಫಾರ್ಮ್ ಬಳಸಿ.",
    "home_profile.add": "ಸೇರಿಸಿ",
    "home_profile.label_type": "ವಿಧ",
    "home_profile.label_area": "ವಿಸ್ತೀರ್ಣ",
    "home_profile.label_sq_ft": "ಚದರ ಅಡಿ",
    "home_profile.label_floors": "ಮಹಡಿಗಳು",
    "home_profile.label_bathrooms": "ಶೌಚಾಲಯಗಳು",
    "home_profile.label_pets": "ಸಾಕು ಪ್ರಾಣಿಗಳು",
    "home_profile.label_kids": "ಮಕ್ಕಳು",
    "home_profile.label_flooring": "ನೆಲ ಹಾಸು",
    "home_profile.label_yes": "ಹೌದು",
    "home_profile.label_no": "ಇಲ್ಲ",
    "home_profile.label_bhk": "BHK",
    "home_profile.floor_ground": "ನೆಲ ಮಹಡಿ",
    "home_profile.floor_unassigned": "ನಿಯೋಜಿಸಿಲ್ಲ",
    "home_profile.sq_ft_up_to": "< {max} ಚದರ ಅಡಿ",
    "home_profile.sq_ft_from": "{min}+ ಚದರ ಅಡಿ",
    "home_profile.sq_ft_range": "{min}–{max} ಚದರ ಅಡಿ",
    "home_profile.rooms_count": "{n} ಕೊಠಡಿಗಳು · {floors} ಮಹಡಿ",
    "home_profile.rooms_count_plural": "{n} ಕೊಠಡಿಗಳು · {floors} ಮಹಡಿಗಳು",
    "home_profile.pets_label": "ಸಾಕು ಪ್ರಾಣಿಗಳು",
    "home_profile.kids_label": "ಮಕ್ಕಳು",
    "home_profile.discard": "ರದ್ದು ಮಾಡಿ",
    "home_profile.back": "ಹಿಂದೆ",
    "home_profile.next": "ಮುಂದೆ",
    "home_profile.save": "ಉಳಿಸಿ",
    "home_profile.edit": "ತಿದ್ದು",
    "home_profile.close": "ಮುಚ್ಚಿ",
    "home_profile.subtitle": "ನಿಮ್ಮ ಮನೆಯ ಕೊಠಡಿಗಳು, ವಿನ್ಯಾಸ ಮತ್ತು ಕುಟುಂಬದ ವಿವರಗಳನ್ನು ನಿರ್ವಹಿಸಿ.",
    "home_profile.empty_title": "ಇನ್ನೂ ಯಾವುದೇ ಮನೆ ಪ್ರೊಫೈಲ್ ಇಲ್ಲ",
    "home_profile.create_button": "ಮನೆ ಪ್ರೊಫೈಲ್ ರಚಿಸಿ",
    "home_profile.draft_found": "ಹಿಂದಿನ ಸೆಷನ್‌ನಿಂದ ಉಳಿಸದ ಬದಲಾವಣೆಗಳಿವೆ. ಅವುಗಳನ್ನು ಮರುಸ್ಥಾಪಿಸಬೇಕೆ ಅಥವಾ ತ್ಯಜಿಸಬೇಕೆ?",
    "home_profile.draft_restore": "ಮರುಸ್ಥಾಪಿಸಿ",
    "home_profile.draft_discard": "ತ್ಯಜಿಸಿ",
    "home_profile.draft_restored": "ಉಳಿಸದ ಬದಲಾವಣೆಗಳನ್ನು ಮರುಸ್ಥಾಪಿಸಲಾಗಿದೆ. ಸಂಪಾದನೆ ಮುಂದುವರಿಸಿ ಮತ್ತು ಉಳಿಸಿ ಕ್ಲಿಕ್ ಮಾಡಿ.",

    "onboarding.welcome_title": "HomeOps ಗೆ ಸ್ವಾಗತ",
    "onboarding.welcome_subtitle": "ನಿಮ್ಮ AI-ಚಾಲಿತ ಮನೆ ನಿರ್ವಹಣೆ ಸಹಾಯಕ",
    "onboarding.welcome_bullet_1": "ಕೆಲಸಗಳು, ಹೆಲ್ಪರ್‌ಗಳು ಮತ್ತು ವೇಳಾಪಟ್ಟಿಗಳನ್ನು ಸುಲಭವಾಗಿ ನಿರ್ವಹಿಸಿ",
    "onboarding.welcome_bullet_2": "AI ಏಜೆಂಟ್ ಸ್ವಯಂಚಾಲಿತವಾಗಿ ಸಮನ್ವಯ ಮತ್ತು ಹೊಂದಾಣಿಕೆ ಮಾಡುತ್ತದೆ",
    "onboarding.welcome_bullet_3": "ಇಂಗ್ಲಿಷ್, ಹಿಂದಿ ಮತ್ತು ಕನ್ನಡದಲ್ಲಿ ಲಭ್ಯ",
    "onboarding.get_started": "ಪ್ರಾರಂಭಿಸೋಣ",
    "onboarding.step_welcome": "ಸ್ವಾಗತ",
    "onboarding.step_profile": "ನಿಮ್ಮ ಪ್ರೊಫೈಲ್",
    "onboarding.step_home": "ನಿಮ್ಮ ಮನೆ",
    "onboarding.step_agent": "ಏಜೆಂಟ್ ಭೇಟಿ ಮಾಡಿ",
    "onboarding.profile_name_title": "ನಿಮ್ಮ ಬಗ್ಗೆ ಹೇಳಿ",
    "onboarding.profile_name_label": "ನಿಮ್ಮ ಹೆಸರು",
    "onboarding.role_title": "ಮನೆಯಲ್ಲಿ ನಿಮ್ಮ ಪಾತ್ರ ಏನು?",
    "onboarding.role_subtitle": "ಯಾರು ಏನನ್ನು ನಿರ್ವಹಿಸುತ್ತಾರೆ ಎಂಬುದನ್ನು ಏಜೆಂಟ್ ಅರ್ಥಮಾಡಿಕೊಳ್ಳಲು ಇದು ಸಹಾಯ ಮಾಡುತ್ತದೆ.",
    "onboarding.role_primary_manager": "ಪ್ರಾಥಮಿಕ ನಿರ್ವಾಹಕ",
    "onboarding.role_primary_manager_desc": "ನಾನು ದಿನನಿತ್ಯ ಮನೆ ನಡೆಸುತ್ತೇನೆ",
    "onboarding.role_shared_responsibility": "ಹಂಚಿಕೊಂಡ ಜವಾಬ್ದಾರಿ",
    "onboarding.role_shared_responsibility_desc": "ನಾವು ಮನೆಯ ಕೆಲಸಗಳನ್ನು ಸಮಾನವಾಗಿ ಹಂಚಿಕೊಳ್ಳುತ್ತೇವೆ",
    "onboarding.role_contributor": "ಕೊಡುಗೆದಾರ",
    "onboarding.role_contributor_desc": "ನಾನು ನಿರ್ದಿಷ್ಟ ಕೆಲಸಗಳಲ್ಲಿ ಸಹಾಯ ಮಾಡುತ್ತೇನೆ",
    "onboarding.role_observer": "ವೀಕ್ಷಕ",
    "onboarding.role_observer_desc": "ನಾನು ಹೆಚ್ಚಾಗಿ ಏನಾಗುತ್ತಿದೆ ಎಂದು ನೋಡುತ್ತೇನೆ",
    "onboarding.goals_title": "ನಿಮಗೆ ಯಾವುದು ಅತ್ಯಂತ ಮುಖ್ಯ?",
    "onboarding.goals_subtitle": "ಒಂದು ಅಥವಾ ಹೆಚ್ಚಿನ ಆದ್ಯತೆಗಳನ್ನು ಆಯ್ಕೆಮಾಡಿ. ಏಜೆಂಟ್ ಅದಕ್ಕೆ ತಕ್ಕಂತೆ ಸಲಹೆಗಳನ್ನು ಸರಿಹೊಂದಿಸುತ್ತದೆ.",
    "onboarding.goal_cleanliness": "ಸ್ವಚ್ಛತೆ",
    "onboarding.goal_health_nutrition": "ಆರೋಗ್ಯ ಮತ್ತು ಪೋಷಣೆ",
    "onboarding.goal_cost_optimization": "ವೆಚ್ಚ ಆಪ್ಟಿಮೈಸೇಶನ್",
    "onboarding.goal_time_saving": "ಸಮಯ ಉಳಿತಾಯ",
    "onboarding.language_title": "ಆದ್ಯತೆಯ ಭಾಷೆ",
    "onboarding.language_subtitle": "ನೀವು ಸಹಾಯಕನ ಜೊತೆ ಬಳಸಲು ಬಯಸುವ ಭಾಷೆಯನ್ನು ಆಯ್ಕೆಮಾಡಿ.",
    "onboarding.schedule_title": "ನಿಮ್ಮ ಲಭ್ಯತೆ",
    "onboarding.schedule_subtitle": "ನೀವು ಸಾಮಾನ್ಯವಾಗಿ ಯಾವಾಗ ಮುಕ್ತರು? ಇದು ಐಚ್ಛಿಕ — ಏಜೆಂಟ್‌ಗೆ ನಿಮ್ಮ ವೇಳಾಪಟ್ಟಿಗೆ ಅನುಗುಣವಾಗಿ ಯೋಜಿಸಲು ಸಹಾಯ ಮಾಡುತ್ತದೆ.",
    "onboarding.schedule_hours": "ಕೆಲಸದ ಸಮಯ",
    "onboarding.schedule_hours_help": "ಉದಾ. 9-18 ಅಂದರೆ ಬೆಳಿಗ್ಗೆ 9 ರಿಂದ ಸಂಜೆ 6 ಗಂಟೆ",
    "onboarding.schedule_skip": "ಈ ಹಂತವನ್ನು ಬಿಡಿ",
    "onboarding.agent_title": "ನಿಮ್ಮ ಮನೆ ಸಹಾಯಕನನ್ನು ಭೇಟಿ ಮಾಡಿ",
    "onboarding.agent_subtitle": "ನಿಮ್ಮ AI ಏಜೆಂಟ್ ಇವೆಲ್ಲಾ ಮತ್ತು ಇನ್ನೂ ಹೆಚ್ಚಿನದರಲ್ಲಿ ಸಹಾಯ ಮಾಡಬಹುದು:",
    "onboarding.agent_example_1": "\"ಎಲ್ಲಾ ಕೊಠಡಿಗಳಿಗೆ ದೈನಂದಿನ ಸ್ವಚ್ಛತೆ ಸೆಟ್ ಮಾಡಿ\"",
    "onboarding.agent_example_2": "\"ಕುಕ್‌ಗೆ ವೀಕ್‌ಡೇಸ್‌ನಲ್ಲಿ ಊಟ ಮಾಡಲು ಹೇಳಿ\"",
    "onboarding.agent_example_3": "\"ನನ್ನ ಹೆಲ್ಪರ್ ಮುಂದಿನ ವಾರ ರಜೆಯಲ್ಲಿದ್ದಾರೆ — ವೇಳಾಪಟ್ಟಿ ಬದಲಾಯಿಸಿ\"",
    "onboarding.agent_example_4": "\"ಇಂದು ಯಾವ ಕೆಲಸಗಳು ಬಾಕಿ ಇವೆ?\"",
    "onboarding.finish": "HomeOps ಬಳಸಲು ಪ್ರಾರಂಭಿಸಿ",
    "onboarding.skip_all": "ಈಗ ಬಿಡಿ",
    "onboarding.name_hint": "ಇದು ನಿಮ್ಮ ಏಜೆಂಟ್‌ಗೆ ಅನುಭವವನ್ನು ವೈಯಕ್ತೀಕರಿಸಲು ಸಹಾಯ ಮಾಡುತ್ತದೆ.",
    "onboarding.your_name": "ನಿಮ್ಮ ಹೆಸರು",
    "onboarding.continue_to_agent": "ಮುಂದುವರಿಸಿ — ನಿಮ್ಮ ಏಜೆಂಟ್ ಅನ್ನು ಭೇಟಿ ಮಾಡಿ",
    "onboarding.confirm_selection": "ಆಯ್ಕೆಯನ್ನು ದೃಢೀಕರಿಸಿ",
    "onboarding.confirm_rooms": "ಕೊಠಡಿಗಳನ್ನು ದೃಢೀಕರಿಸಿ",
    "onboarding.select_features": "ನಿಮ್ಮ ಮನೆಯಲ್ಲಿ ಏನಿದೆ?",
    "onboarding.confirm_features": "ವೈಶಿಷ್ಟ್ಯಗಳನ್ನು ದೃಢೀಕರಿಸಿ",
    "onboarding.confirm_details": "ವಿವರಗಳನ್ನು ದೃಢೀಕರಿಸಿ",
    "onboarding.review_chores": "ಸೂಚಿಸಿದ ಕೆಲಸಗಳನ್ನು ಪರಿಶೀಲಿಸಿ",
    "onboarding.create_chores": "ಕೆಲಸಗಳನ್ನು ರಚಿಸಿ",
    "onboarding.review_chores_hint": "ಶೀರ್ಷಿಕೆ ಮತ್ತು ಆವರ್ತನ ಸಂಪಾದಿಸಿ, ಅನಗತ್ಯ ಕೆಲಸಗಳನ್ನು ಅನ್‌ಚೆಕ್ ಮಾಡಿ.",
    "onboarding.skip_chores": "ಈಗ ಬಿಡಿ",
    "onboarding.add_helper": "ನಿಮ್ಮ ಮನೆಯ ಸಹಾಯಕರನ್ನು ಸೇರಿಸಿ",
    "onboarding.add_helpers_hint": "ಎಲ್ಲಾ ಸಹಾಯಕರನ್ನು ಸೇರಿಸಿ, ನಂತರ ಒಟ್ಟಿಗೆ ಉಳಿಸಿ.",
    "onboarding.add_to_list": "ಪಟ್ಟಿಗೆ ಸೇರಿಸಿ",
    "onboarding.save_helpers": "ಸಹಾಯಕರನ್ನು ಉಳಿಸಿ",
    "onboarding.skip_helpers": "ಸಹಾಯಕರಿಲ್ಲ / ಬಿಡಿ",
    "onboarding.setup_progress": "ಸೆಟಪ್ ಪ್ರಗತಿ",
    "onboarding.skip_step": "ಈ ಹಂತವನ್ನು ಬಿಡಿ",
    "onboarding.finish_later": "ನಂತರ ಮುಗಿಸಿ",
    "onboarding.setup_complete": "ಸೆಟಪ್ ಪೂರ್ಣ!",
    "onboarding.setup_complete_hint": "ನಿಮ್ಮ ಮನೆ ಸೆಟಪ್ ಆಗಿದೆ. ಸೈಡ್‌ಬಾರ್‌ನಿಂದ ಯಾವಾಗ ಬೇಕಾದರೂ ಬದಲಾಯಿಸಬಹುದು.",
    "onboarding.assign_chores_now": "ಸಹಾಯಕರಿಗೆ ಕೆಲಸ ನಿಯೋಜಿಸಿ",
    "onboarding.assign_later": "ನಂತರ ಮಾಡುತ್ತೇನೆ",

    "nav.dashboard": "ಡ್ಯಾಶ್‌ಬೋರ್ಡ್",
    "nav.home_profile": "ಮನೆ ಪ್ರೊಫೈಲ್",
    "nav.chores": "ಮನೆಕೆಲಸಗಳು",
    "nav.coverage": "ಕವರೇಜ್",
    "nav.events": "ಘಟನೆಗಳು",
    "nav.recipes": "ಪಾಕವಿಧಾನಗಳು",
    "nav.helpers": "ಸಹಾಯಕರು",
    "nav.services": "ಸೇವೆಗಳು",
    "nav.maintenance": "ನಿರ್ವಹಣೆ",
    "nav.alerts": "ಸೂಚನೆಗಳು",
    "nav.automations": "ಸ್ವಯಂಚಾಲನೆಗಳು",
    "nav.signals": "ಸಿಗ್ನಲ್‌ಗಳು",
    "nav.chat": "ಚಾಟ್ ಸಹಾಯಕ",
    "nav.status": "ಕಾರ್ಯ ಸ್ಥಿತಿ",
    "nav.tests": "ಪರೀಕ್ಷೆಗಳು",
    "nav.admin": "ನಿರ್ವಾಹಕ ಸಂರಚನೆ",
    "nav.analytics": "ಅನಾಲಿಟಿಕ್ಸ್",
    "nav.support": "ಬೆಂಬಲ ಫಲಕ",

    "coverage.title": "ಕವರೇಜ್",
    "coverage.subtitle": "ಸಹಾಯಕರು ಮತ್ತು ಸಾಧನಗಳಿಂದ ಏನು ಆವರಿಸಲಾಗಿದೆ ಮತ್ತು ಅಂತರಗಳು ಎಲ್ಲಿವೆ ಎಂದು ನೋಡಿ",
    "coverage.map_title": "ಕವರೇಜ್ ನಕ್ಷೆ",
    "coverage.map_subtitle": "ಆವರ್ತನದ ಪ್ರಕಾರ ಸ್ಥಳಗಳು — ಆವರಿಸುವವರಿಗೆ ಅನುಗುಣವಾಗಿ ಬಣ್ಣ",
    "coverage.gaps_title": "ಕವರೇಜ್ ಅಂತರಗಳು",
    "coverage.gaps_subtitle": "ಯಾವುದೇ ಕೆಲಸ ಮತ್ತು ಆಟೊಮೇಷನ್ ಇಲ್ಲದ ಸ್ಥಳಗಳು",
    "coverage.empty_title": "ಇನ್ನೂ ಯಾವುದೇ ಕವರೇಜ್ ಡೇಟಾ ಇಲ್ಲ",
    "coverage.empty_subtitle": "ಕವರೇಜ್ ಪ್ಲಾನರ್ ಬಳಸಿ ನಿಮ್ಮ ಮನೆಯ ಪ್ರತಿ ಸ್ಥಳದ ಬಗ್ಗೆ ತಿಳಿಸಿ ಮತ್ತು ವೈಯಕ್ತಿಕ ಕೆಲಸ ಪಟ್ಟಿಯನ್ನು ಪಡೆಯಿರಿ.",
    "coverage.first_time_title": "ನಿಮ್ಮ ಆರಂಭಿಕ ಕೆಲಸಗಳನ್ನು ಸೆಟ್ ಮಾಡಿ",
    "coverage.first_time_subtitle": "ನಿಮ್ಮ ಮನೆ ಪ್ರೊಫೈಲ್‌ನಲ್ಲಿ {count} ಸ್ಥಳಗಳನ್ನು ಕಂಡುಕೊಂಡಿದ್ದೇವೆ. ಆರಂಭಿಕ ಕೆಲಸ ಪಟ್ಟಿ ರಚಿಸಲು ಪ್ಲಾನರ್ ಬಳಸಿ.",
    "coverage.audit_button": "ಕವರೇಜ್ ಆಡಿಟ್",
    "coverage.audit_view": "ಕವರೇಜ್ ಆಡಿಟ್",
    "coverage.matrix_view": "ಪೂರ್ಣ ಮ್ಯಾಟ್ರಿಕ್ಸ್",
    "coverage.bulk_add_button": "ಸಾಮೂಹಿಕವಾಗಿ ಸೇರಿಸಿ",

    "coverage_audit.title": "ಕವರೇಜ್ ಆಡಿಟ್",
    "coverage_audit.subtitle": "ಏನು ಕಾಣೆಯಾಗಿದೆ ಎಂದು ನೋಡಿ ಮತ್ತು ನಿಮಗೆ ಬೇಕಾದದನ್ನು ಮಾತ್ರ ಸೇರಿಸಿ",
    "coverage_audit.gaps_section": "ಸರಿಪಡಿಸಲು ಅಂತರಗಳು",
    "coverage_audit.full_map_title": "ಪೂರ್ಣ ಕವರೇಜ್ ನಕ್ಷೆಯನ್ನು ತೋರಿಸಿ",
    "coverage_audit.no_spaces": "ನಿಮ್ಮ ಮನೆ ಪ್ರೊಫೈಲ್‌ನಲ್ಲಿ ಇನ್ನೂ ಯಾವುದೇ ಸ್ಥಳಗಳಿಲ್ಲ. ಕವರೇಜ್ ಆಡಿಟ್ ಪ್ರಾರಂಭಿಸಲು ಮೊದಲು ಮನೆ ಪ್ರೊಫೈಲ್ ಸೆಟ್ ಮಾಡಿ.",

    "health.title": "ನಿಮ್ಮ ಮನೆ ಕವರೇಜ್",
    "health.score_label": "ಸ್ಕೋರ್",
    "health.summary": "{total} ಕೊಠಡಿಗಳು — {covered} ಸಂಪೂರ್ಣ ಕವರ್ಡ್, {partial} ಭಾಗಶಃ, {gaps} ಅಂತರಗಳೊಂದಿಗೆ",
    "health.fully_covered": "ಸಂಪೂರ್ಣ ಕವರ್ಡ್",
    "health.partial": "ಭಾಗಶಃ",
    "health.gaps": "ಅಂತರಗಳು",
    "health.fix_all": "{count} ಅಂತರಗಳನ್ನು ಸರಿಪಡಿಸಿ",
    "health.critical_gaps_label": "{count} ನಿರ್ಣಾಯಕ",

    "gaps.severity_critical": "ನಿರ್ಣಾಯಕ",
    "gaps.severity_important": "ಮುಖ್ಯ",
    "gaps.severity_nice": "ಒಳ್ಳೆಯದು",
    "gaps.critical_intro": "ಈ ಅಂತರಗಳು ದೈನಂದಿನ ನೈರ್ಮಲ್ಯ ಮತ್ತು ಸುರಕ್ಷತೆಯ ಮೇಲೆ ಪರಿಣಾಮ ಬೀರುತ್ತವೆ. ಮೊದಲು ಇವುಗಳನ್ನು ಸರಿಪಡಿಸಲು ಶಿಫಾರಸು ಮಾಡಲಾಗಿದೆ.",
    "gaps.empty_title": "ಎಲ್ಲವೂ ಕವರ್ಡ್!",
    "gaps.empty_subtitle": "ಪ್ರತಿ ಶಿಫಾರಸು ಮಾಡಿದ ಕೆಲಸವೂ ಇದೆ. ನಿಮ್ಮ ಮನೆ ಚೆನ್ನಾಗಿ ಕವರ್ ಆಗಿದೆ.",
    "gaps.chore_title": "ಕೆಲಸದ ಶೀರ್ಷಿಕೆ",
    "gaps.add_chore": "ಕೆಲಸ ಸೇರಿಸಿ",
    "gaps.dismiss": "ಬೇಕಾಗಿಲ್ಲ",
    "gaps.added_success": "\"{title}\" ಅನ್ನು ನಿಮ್ಮ ಕೆಲಸಗಳಿಗೆ ಸೇರಿಸಲಾಗಿದೆ.",
    "gaps.dismissed_success": "ಸರಿ — ನಾವು ಇದನ್ನು ಮತ್ತೆ ಸೂಚಿಸುವುದಿಲ್ಲ.",
    "gaps.fix_all_success": "{count} ಕೆಲಸಗಳನ್ನು ರಚಿಸಲಾಗಿದೆ.",
    "gaps.fix_all_partial": "{added} ಕೆಲಸಗಳನ್ನು ಸೇರಿಸಲಾಗಿದೆ, {failed} ವಿಫಲವಾಗಿವೆ.",

    "engine.sync_schedule": "ವೇಳಾಪಟ್ಟಿ ಸಿಂಕ್",
    "engine.syncing": "ಸಿಂಕ್ ಆಗುತ್ತಿದೆ…",
    "engine.sync_results": "ವೇಳಾಪಟ್ಟಿ ಸಿಂಕ್",
    "engine.sync_summary": "{scheduled} ಹೊಸ ಕೆಲಸಗಳನ್ನು ಶೆಡ್ಯೂಲ್ ಮಾಡಬೇಕು, {adjusted} ಹೊಂದಾಣಿಕೆಗಳು ಶಿಫಾರಸು",
    "engine.section_scheduled": "ಶೆಡ್ಯೂಲ್ ಮಾಡಲು ಹೊಸ ಕೆಲಸಗಳು",
    "engine.section_adjustments": "ಹೊಂದಾಣಿಕೆಗಳು",
    "engine.apply": "ಅನ್ವಯಿಸಿ",
    "engine.applied": "ಅನ್ವಯಿಸಲಾಗಿದೆ",
    "engine.apply_all": "ಎಲ್ಲವನ್ನೂ ಅನ್ವಯಿಸಿ ({count})",
    "engine.edit_title": "ಕೆಲಸದ ಶೀರ್ಷಿಕೆ",
    "engine.nothing_to_sync": "ಎಲ್ಲವೂ ನವೀಕೃತವಾಗಿದೆ. ಯಾವುದೇ ಬದಲಾವಣೆ ಅಗತ್ಯವಿಲ್ಲ.",
    "engine.tasks_across_rooms": "{rooms} ಕೊಠಡಿಗಳಲ್ಲಿ",
    "engine.apply_room": "ಕೊಠಡಿ ಅನ್ವಯಿಸಿ",
    "engine.escalation_title": "ಹಸ್ತಕ್ಷೇಪ ಅಗತ್ಯ — ಕೆಲಸಗಳು ಅಪಾಯದಲ್ಲಿವೆ",

    "planner.empty_coverage": "ಇನ್ನೂ ಯಾವುದೇ ಸ್ಥಳಗಳು ಕಂಡುಬಂದಿಲ್ಲ. ಪ್ಲಾನರ್ ಬಳಸಿ ನಿಮ್ಮ ಸ್ಥಳಗಳನ್ನು ಸೆಟ್ ಮಾಡಿ.",

    "events.title": "ಮನೆ ಘಟನೆಗಳು",
    "events.subtitle": "ಅತಿಥಿಗಳು, ರಜೆಗಳು, ಸಂದರ್ಭಗಳು ಮತ್ತು ಇತರ ಘಟನೆಗಳನ್ನು ಟ್ರ್ಯಾಕ್ ಮಾಡಿ",
    "events.add_event": "ಘಟನೆ ಸೇರಿಸಿ",
    "events.empty": "ಇನ್ನೂ ಯಾವುದೇ ಘಟನೆಗಳಿಲ್ಲ. ಅತಿಥಿ, ರಜೆ ಅಥವಾ ವಿಶೇಷ ಸಂದರ್ಭ ಸೇರಿಸಿ.",
    "events.type": "ಪ್ರಕಾರ",
    "events.start_at": "ಪ್ರಾರಂಭ ದಿನಾಂಕ/ಸಮಯ",
    "events.end_at": "ಮುಕ್ತಾಯ ದಿನಾಂಕ/ಸಮಯ",
    "events.end_at_optional": "ಐಚ್ಛಿಕ",
    "events.notes": "ಟಿಪ್ಪಣಿಗಳು",
    "events.type_guest_arrival": "ಅತಿಥಿ ಆಗಮನ",
    "events.type_vacation": "ರಜೆ",
    "events.type_occasion": "ವಿಶೇಷ ಸಂದರ್ಭ",
    "events.type_weather": "ಹವಾಮಾನ ಘಟನೆ",
    "events.type_member_health": "ಸದಸ್ಯ ಆರೋಗ್ಯ",
    "events.type_helper_leave": "ಸಹಾಯಕ ರಜೆ",
    "events.replan_hint": "ಘಟನೆ ಉಳಿಸಲಾಗಿದೆ — ಕೆಲಸದ ಸಲಹೆಗಳು ಬದಲಾಗಿರಬಹುದು",

    "helpers.workload_title": "ಸಹಾಯಕ ಕೆಲಸದ ಭಾರ",
    "helpers.workload_subtitle": "ಇಂದಿನ ಕೆಲಸಗಳು ಮತ್ತು ಸಾಮರ್ಥ್ಯ ಬಳಕೆ",
    "helpers.overdue": "ಬಾಕಿ",
    "helpers.no_capacity_set": "ದೈನಂದಿನ ಸಾಮರ್ಥ್ಯ ಸೆಟ್ ಮಾಡಿಲ್ಲ",
    "helpers.over_capacity": "ಸಾಮರ್ಥ್ಯಕ್ಕಿಂತ ಹೆಚ್ಚು — ಪುನರ್ಸಮತೋಲನವನ್ನು ಪರಿಗಣಿಸಿ",

    "chores.bulk_reassign_title": "ಕೆಲಸಗಳನ್ನು ಸಾಮೂಹಿಕವಾಗಿ ಮರು ನಿಯೋಜಿಸಿ",
    "chores.bulk_reassign_subtitle": "ಪ್ರಸ್ತುತ {name} ಗೆ ನಿಯೋಜಿಸಲಾದ ಕೆಲಸಗಳನ್ನು ಮರು ನಿಯೋಜಿಸಿ",
    "chores.reassign_to": "ಮರು ನಿಯೋಜಿಸಿ",
    "chores.no_chores_to_reassign": "ಮರು ನಿಯೋಜಿಸಲು ಯಾವುದೇ ಸಕ್ರಿಯ ಕೆಲಸಗಳಿಲ್ಲ",
    "chores.selected_count": "{count} / {total} ಆಯ್ಕೆ",
    "chores.bulk_reassign_apply": "{count} ಮರು ನಿಯೋಜಿಸಿ",

    "replan.title": "ಸ್ಮಾರ್ಟ್ ಸಲಹೆಗಳು",
    "replan.subtitle": "ಘಟನೆಗಳು, ಕವರೇಜ್ ಮತ್ತು ಕೆಲಸದ ಆಧಾರದ ಮೇಲೆ {count} ಸಲಹೆಗಳು",
    "replan.button": "ಸಲಹೆಗಳು",
    "replan.empty": "ಈಗ ಯಾವುದೇ ಸಲಹೆಗಳಿಲ್ಲ. ಪ್ರಾರಂಭಿಸಲು ಮನೆ ಘಟನೆಗಳನ್ನು ಸೇರಿಸಿ ಅಥವಾ ಕವರೇಜ್ ಪರಿಶೀಲಿಸಿ.",
    "replan.dismiss": "ತಿರಸ್ಕರಿಸಿ",
    "replan.severity_info": "ಮಾಹಿತಿ",
    "replan.severity_warning": "ಎಚ್ಚರಿಕೆ",
    "replan.severity_critical": "ನಿರ್ಣಾಯಕ",
    "replan.kind_reassign_helper": "ಮರು ನಿಯೋಜಿಸಿ",
    "replan.kind_add_chore": "ಕೆಲಸ ಸೇರಿಸಿ",
    "replan.kind_skip_chore": "ಬಿಡಿ",
    "replan.kind_balance_workload": "ಪುನರ್ಸಮತೋಲನ",
    "replan.kind_coverage_gap": "ಕವರೇಜ್",

    "chores.title": "ಕೆಲಸಗಳು",
    "chores.subtitle": "ಮನೆಯ ಕೆಲಸಗಳು ಮತ್ತು ನಿಯೋಜನೆಗಳನ್ನು ನಿರ್ವಹಿಸಿ",
    "chores.back_to_coverage": "ಕವರೆಜ್‌ಗೆ ಹಿಂತಿರುಗಿ",
    "chores.view_all": "ಎಲ್ಲಾ ಕೆಲಸಗಳನ್ನು ನೋಡಿ",
    "chores.daily_chores": "ದೈನಂದಿನ ಕೆಲಸಗಳು",
    "chores.add_chore": "ಕೆಲಸ ಸೇರಿಸಿ",
    "chores.remove_duplicates": "ನಕಲುಗಳನ್ನು ತೆಗೆದುಹಾಕಿ",
    "chores.coverage": "ಕವರೆಜ್",
    "chores.planned_frequency": "ಸ್ಥಳದ ಪ್ರಕಾರ ಯೋಜಿತ ಅವಧಿ",
    "chores.edit_baseline": "ಬೇಸ್‌ಲೈನ್ ತಿದ್ದು",
    "chores.autofill_missing": "ಕಡಿಮೆ ಇರುವ ಕವರೆಜ್ ಅನ್ನು ಆಟೋ-ಫಿಲ್ ಮಾಡಿ",
    "chores.no_chores_yet": "ಇನ್ನೂ ಯಾವುದೇ ಕೆಲಸಗಳಿಲ್ಲ.",
    "chores.daily": "ದೈನಂದಿನ",
    "chores.weekly": "ವಾರದ",
    "chores.biweekly": "ಎರಡು ವಾರಕ್ಕೆ",
    "chores.monthly": "ತಿಂಗಳ",
    "chores.assign_to": "ನಿಯೋಜಿಸಿ",
    "chores.unassigned": "ನಿಯೋಜಿಸಿಲ್ಲ",
    "chores.current": "ಪ್ರಸ್ತುತ",
    "chores.due": "ಗಡುವು",
    "chores.no_duplicates_found": "ಯಾವುದೇ ನಕಲುಗಳು ಕಂಡುಬಂದಿಲ್ಲ.",
    "chores.no_localization_needed": "ಯಾವುದೇ ಕೆಲಸಕ್ಕೆ ಲೋಕಲೈಝೇಶನ್ ಅಗತ್ಯವಿಲ್ಲ.",
    "chores.localized": "ಲೋಕಲೈಸ್ ಮಾಡಲಾಗಿದೆ",
    "chores.chore_s": "ಕೆಲಸ(ಗಳು).",
    "chores.removed": "ತೆಗೆದುಹಾಕಲಾಗಿದೆ",
    "chores.duplicate_chore_s": "ನಕಲಿ ಕೆಲಸ(ಗಳು).",
    "chores.assigned_to": "ನಿಯೋಜಿಸಲಾಗಿದೆ",
    "chores.reported_makeup_created": "ರಿಪೋರ್ಟ್ ಮಾಡಲಾಗಿದೆ. ಮೆಕ್-ಅಪ್ ಕೆಲಸ ಸೃಷ್ಟಿಸಲಾಗಿದೆ.",
    "chores.baseline_updated": "ಬೇಸ್‌ಲೈನ್ ನವೀಕರಿಸಲಾಗಿದೆ.",
    "chores.missing_baseline_draft": "ಬೇಸ್‌ಲೈನ್ ಡ್ರಾಫ್ಟ್ ಕಾಣೆಯಾಗಿದೆ.",
    "chores.no_home_profile": "ಯಾವುದೇ ಹೋಮ್ ಪ್ರೊಫೈಲ್ ಕಂಡುಬಂದಿಲ್ಲ. ಮೊದಲು ನಿಮ್ಮ ಹೋಮ್ ಪ್ರೊಫೈಲ್ ರಚಿಸಿ (ಚಾಟ್ → ಹೋಮ್ ಪ್ರೊಫೈಲ್), ನಂತರ ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.",
    "chores.aria_report_not_done": "ಮುಗಿಯಲಿಲ್ಲ ಎಂದು ವರದಿ ಮಾಡಿ",
    "chores.aria_edit_chore": "ಕೆಲಸ ತಿದ್ದು",
    "chores.date": "ದಿನಾಂಕ",
    "chores.time_zone": "ಸಮಯ ವಲಯ",
    "chores.only_daily_cadence": "ದೈನಂದಿನ ಕ್ಯಾಡೆನ್ಸ್ ಮಾತ್ರ",
    "chores.daily_counts": "ದೈನಂದಿನ:",
    "chores.inferred_daily_counts": "ಅಂದಾಜು ದೈನಂದಿನ:",
    "chores.no_chores_found": "ಯಾವುದೇ ಕೆಲಸಗಳು ಕಂಡುಬಂದಿಲ್ಲ",
    "chores.no_chores_due_on_date": "ಈ ದಿನಾಂಕಕ್ಕೆ ಯಾವುದೇ ಕೆಲಸದ ಗಡುವಿಲ್ಲ.",
    "chores.filters": "ಫಿಲ್ಟರ್‌ಗಳು",
    "chores.caught_up": "ನೀವು ಎಲ್ಲವನ್ನೂ ಮುಗಿಸಿದ್ದೀರಿ!",
    "chores.create_new": "ಹೊಸ ಕೆಲಸ ರಚಿಸಿ",
    "chores.chore_title": "ಕೆಲಸದ ಶೀರ್ಷಿಕೆ",
    "chores.description": "ವಿವರಣೆ",
    "chores.category": "ವರ್ಗ",
    "chores.due_date": "ಗಡುವು ದಿನಾಂಕ",
    "chores.create_chore": "ಕೆಲಸ ರಚಿಸಿ",
    "chores.chore_updated": "ಕೆಲಸ ನವೀಕರಿಸಲಾಗಿದೆ.",
    "chores.no_recommendations_selected": "ಯಾವುದೇ ಶಿಫಾರಸುಗಳನ್ನು ಆಯ್ಕೆ ಮಾಡಿಲ್ಲ.",
    "chores.created": "ಸೃಷ್ಟಿಸಲಾಗಿದೆ",
    "chores.makeup_already_exists": "ಈ ಕೆಲಸಕ್ಕೆ ಮೆಕ್-ಅಪ್ ಕೆಲಸ ಈಗಾಗಲೇ ಇದೆ.",
    "chores.edit_coverage_baseline": "ಕವರೆಜ್ ಬೇಸ್‌ಲೈನ್ ತಿದ್ದು",
    "chores.edit_baseline_help": "ಪ್ರತಿ ಸ್ಪೇಸ್ ವಿಧಕ್ಕೆ ಗುರಿ ಕ್ಯಾಡೆನ್ಸ್‌ಗಳನ್ನು ಆಯ್ಕೆಮಾಡಿ. ಕವರೆಜ್ ಹೀಟ್‌ಮ್ಯಾಪ್ ಕೊರತೆಯಿರುವ ಗುರಿ ಕೆಲಸಗಳನ್ನು ಹೈಲೈಟ್ ಮಾಡುತ್ತದೆ.",
    "chores.save_baseline": "ಬೇಸ್‌ಲೈನ್ ಉಳಿಸಿ",
    "chores.autofill_help": "ಮುಖ್ಯ ಸ್ಪೇಸ್‌ಗಳಿಗೆ ಕವರೆಜ್ ಸುಧಾರಿಸಲು ಶಿಫಾರಸು ಮಾಡಿದ ಕೆಲಸಗಳನ್ನು ಪರಿಶೀಲಿಸಿ (ಅಡುಗೆಮನೆ, ಸ್ನಾನಗೃಹಗಳು, ಬಾಲ್ಕನಿ, ಟೆರೇಸ್, ಗ್ಯಾರೇಜ್ ಇತ್ಯಾದಿ).",
    "chores.prepared": "ತಯಾರಿಸಲಾಗಿದೆ",
    "chores.recommendation_s": "ಶಿಫಾರಸು(ಗಳು).",
    "chores.no_missing_coverage": "ಯಾವುದೇ ಕೊರತೆಯ ಕವರೆಜ್ ಕಂಡುಬಂದಿಲ್ಲ.",
    "chores.create_chores": "ಕೆಲಸಗಳನ್ನು ರಚಿಸಿ",
    "chores.localize_title": "ಕೆಲಸದ ಪದಗಳನ್ನು ಲೋಕಲೈಸ್ ಮಾಡಿ",
    "chores.localize_help": "ಇದು ಭಾರತೀಯ ಸಂದರ್ಭಕ್ಕೆ ಹೊಂದಿಕೊಳ್ಳುವಂತೆ ಕೆಲಸದ ಶೀರ್ಷಿಕೆ/ವಿವರಣೆಯನ್ನು ನವೀಕರಿಸುತ್ತದೆ (ಉದಾ., “vacuum” → “sweep and mop”).",
    "chores.this_will_update": "ಇದು ನವೀಕರಿಸುತ್ತದೆ",
    "chores.no_chores_need_localization": "ಯಾವುದೇ ಕೆಲಸಕ್ಕೆ ಲೋಕಲೈಝೇಶನ್ ಅಗತ್ಯವಿಲ್ಲ.",
    "chores.update_chores": "ಕೆಲಸಗಳನ್ನು ನವೀಕರಿಸಿ",
    "chores.edit_chore": "ಕೆಲಸ ತಿದ್ದು",
    "chores.title_label": "ಶೀರ್ಷಿಕೆ",
    "chores.status": "ಸ್ಥಿತಿ",
    "chores.status_pending": "pending",
    "chores.status_in_progress": "in-progress",
    "chores.status_completed": "completed",
    "chores.priority": "ಪ್ರಾಮುಖ್ಯತೆ (1-3)",
    "chores.space": "ಸ್ಪೇಸ್",
    "chores.subspace": "ಸಬ್‌ಸ್ಪೇಸ್",
    "chores.cadence": "ಕ್ಯಾಡೆನ್ಸ್",
    "chores.none": "(ಯಾವುದೂ ಇಲ್ಲ)",
    "chores.helper": "ಹೆಲ್ಪರ್",
    "chores.remove_duplicate_chores": "ನಕಲಿ ಕೆಲಸಗಳನ್ನು ತೆಗೆದುಹಾಕಿ",
    "chores.duplicate_rule": "ನಕಲು ನಿಯಮ: ಸಮಾನ ಶೀರ್ಷಿಕೆ + ಸ್ಪೇಸ್ + ಕ್ಯಾಡೆನ್ಸ್.",
    "chores.keep_strategy": "ಯೋಜನೆ: ಇದ್ದರೆ ಇತ್ತೀಚಿನ ಪೂರ್ಣಗೊಂಡುದನ್ನು ಉಳಿಸಿ, ಇಲ್ಲದಿದ್ದರೆ ಇತ್ತೀಚಿನದನ್ನು ಉಳಿಸಿ.",
    "chores.permanently_delete_prefix": "ಇದು ಶಾಶ್ವತವಾಗಿ ಅಳಿಸುತ್ತದೆ",
    "chores.across": "ಒಟ್ಟು",
    "chores.duplicate_group_s": "ನಕಲಿ ಗುಂಪು(ಗಳು).",
    "chores.delete_duplicates": "ನಕಲುಗಳನ್ನು ಅಳಿಸಿ",
    "chores.helper_view": "ಸಹಾಯಕ ವೀಕ್ಷಣೆ",
    "chores.task_view": "ಕಾರ್ಯ ವೀಕ್ಷಣೆ",
    "chores.report_not_done": "ಪೂರ್ಣವಾಗಿಲ್ಲ ಎಂದು ವರದಿ ಮಾಡಿ",
    "chores.priority_low": "P1 — ಕಡಿಮೆ",
    "chores.priority_medium": "P2 — ಮಧ್ಯಮ",
    "chores.priority_high": "P3 — ಹೆಚ್ಚು",
    "chores.n_selected": "{count} ಆಯ್ಕೆಮಾಡಲಾಗಿದೆ",
    "chores.select_all": "ಎಲ್ಲಾ ಆಯ್ಕೆಮಾಡಿ",
    "chores.delete_selected": "ಆಯ್ಕೆಮಾಡಿದ ಅಳಿಸಿ",
    "chores.confirm_delete": "\"{title}\" ಅಳಿಸುವುದೇ? ಇದನ್ನು ರದ್ದುಗೊಳಿಸಲು ಸಾಧ್ಯವಿಲ್ಲ.",
    "chores.confirm_bulk_delete_title": "{count} ಕೆಲಸ(ಗಳನ್ನು) ಅಳಿಸುವುದೇ?",
    "chores.confirm_bulk_delete_message": "ಇದು ಆಯ್ಕೆಮಾಡಿದ ಕೆಲಸಗಳನ್ನು ಶಾಶ್ವತವಾಗಿ ಅಳಿಸುತ್ತದೆ.",
    "chores.choose_space": "ಸ್ಥಳ ಆಯ್ಕೆಮಾಡಿ",
    "chores.choose_space_error": "ದಯವಿಟ್ಟು ಸ್ಥಳ ಆಯ್ಕೆಮಾಡಿ.",
    "chores.apply": "ಅನ್ವಯಿಸಿ",
    "chores.clear_filters": "ಫಿಲ್ಟರ್‌ಗಳನ್ನು ತೆರವುಗೊಳಿಸಿ",
    "chores.tab_all": "ಎಲ್ಲಾ",
    "chores.tab_pending": "ಬಾಕಿ",
    "chores.tab_in_progress": "ಪ್ರಗತಿಯಲ್ಲಿ",
    "chores.tab_completed": "ಪೂರ್ಣ",
    "chores.empty_title": "ಯಾವುದೇ ಕೆಲಸಗಳು ಕಂಡುಬಂದಿಲ್ಲ",
    "chores.empty_subtitle": "ಕೆಲಸ ಸೇರಿಸಿ ಅಥವಾ ಶೆಡ್ಯೂಲರ್ ಚಲಾಯಿಸಿ.",
    "chores.on_leave_warning": "ಈ ದಿನಾಂಕಕ್ಕೆ ಸಹಾಯಕರು ರಜೆಯಲ್ಲಿದ್ದಾರೆ",
    "task_status.export": "ವರದಿ ರಫ್ತು",
    "task_status.total_tasks": "ಒಟ್ಟು ಕಾರ್ಯಗಳು",
    "task_status.completed": "ಪೂರ್ಣಗೊಂಡ",
    "task_status.in_progress": "ನಡೆಸುತ್ತಿದೆ",
    "task_status.pending": "ಬಾಕಿ",
    "task_status.overdue": "ತಡ",
    "task_status.overall_completion": "ಒಟ್ಟು ಪೂರ್ಣತಾ ದರ",
    "task_status.progress_subtitle": "ಎಲ್ಲಾ ಮನೆಯ ಕಾರ್ಯಗಳ ಪ್ರಗತಿ",
    "task_status.completion_progress": "ಪೂರ್ಣತಾ ಪ್ರಗತಿ",
    "task_status.status": "ಸ್ಥಿತಿ",
    "task_status.time_period": "ಸಮಯಾವಧಿ",
    "task_status.all": "ಎಲ್ಲಾ",
    "task_status.all_time": "ಎಲ್ಲಾ ಸಮಯ",
    "task_status.today": "ಇಂದು",
    "task_status.this_week": "ಈ ವಾರ",
    "task_status.this_month": "ಈ ತಿಂಗಳು",
    "task_status.assigned_to": "ಜವಾಬ್ದಾರಿ",
    "task_status.due_date": "ಗಡುವು ದಿನಾಂಕ",
    "task_status.notes": "ಟಿಪ್ಪಣಿಗಳು",

    "analytics.title": "ವಿಶ್ಲೇಷಣೆ ಮತ್ತು ಬಳಕೆ",
    "analytics.subtitle": "ಅ್ಯಪ್ ಕಾರ್ಯಕ್ಷಮತೆ ಮತ್ತು ಬಳಕೆದಾರ ತೊಡಗಿಸಿಕೊಳ್ಳುವಿಕೆ ನೋಡಿ",
    "analytics.time_range": "ಸಮಯ ಮಿತಿ",
    "analytics.last_24_hours": "ಕಳೆದ 24 ಗಂಟೆಗಳು",
    "analytics.last_7_days": "ಕಳೆದ 7 ದಿನಗಳು",
    "analytics.last_30_days": "ಕಳೆದ 30 ದಿನಗಳು",
    "analytics.last_90_days": "ಕಳೆದ 90 ದಿನಗಳು",
    "analytics.export": "ರಫ್ತು",
    "analytics.from_last_period": "ಹಿಂದಿನ ಅವಧಿಯಿಂದ",
    "analytics.feature_usage": "ಫೀಚರ್ ಬಳಕೆ",
    "analytics.feature_usage_subtitle": "ಅಪನಯನ ದರದ ಪ್ರಕಾರ ಜನಪ್ರಿಯ ಫೀಚರ್‌ಗಳು",
    "analytics.users": "ಬಳಕೆದಾರರು",
    "analytics.adoption_rate": "ಅಪನಯನ ದರ",
    "analytics.recent_activity": "ಇತ್ತೀಚಿನ ಚಟುವಟಿಕೆ",
    "analytics.recent_activity_subtitle": "ಮನೆಗಳಿಂದ ಲೈವ್ ಅಪ್‌ಡೇಟ್‌ಗಳು",
    "analytics.user_growth": "ಸಮಯದೊಂದಿಗೆ ಬಳಕೆದಾರ ಬೆಳವಣಿಗೆ",
    "analytics.user_growth_subtitle": "ಹೊಸ ನೋಂದಣಿ ಮತ್ತು ಮನೆ ಬೆಳವಣಿಗೆ",
    "analytics.chart_placeholder": "ಇಲ್ಲಿ ಚಾರ್ಟ್ ಕಾಣಿಸುತ್ತದೆ",

    "owner_analytics.title": "ಓನರ್ ವಿಶ್ಲೇಷಣೆ",
    "owner_analytics.subtitle": "ಮನೆ ಪ್ರದರ್ಶನ ಮತ್ತು ಬಳಕೆದಾರ ತೊಡಗಿಸಿಕೊಳ್ಳುವಿಕೆ ನೋಡಿ",
    "owner_analytics.time_range": "ಸಮಯ ಮಿತಿ",
    "owner_analytics.last_24_hours": "ಕಳೆದ 24 ಗಂಟೆಗಳು",
    "owner_analytics.last_7_days": "ಕಳೆದ 7 ದಿನಗಳು",
    "owner_analytics.last_30_days": "ಕಳೆದ 30 ದಿನಗಳು",
    "owner_analytics.last_90_days": "ಕಳೆದ 90 ದಿನಗಳು",
    "owner_analytics.export": "ರಫ್ತು",
    "owner_analytics.from_last_period": "ಹಿಂದಿನ ಅವಧಿಯಿಂದ",
    "owner_analytics.feature_usage": "ಫೀಚರ್ ಬಳಕೆ",
    "owner_analytics.feature_usage_subtitle": "ಅಪನಯನ ದರದ ಪ್ರಕಾರ ಜನಪ್ರಿಯ ಫೀಚರ್‌ಗಳು",
    "owner_analytics.users": "ಬಳಕೆದಾರರು",
    "owner_analytics.adoption_rate": "ಅಪನಯನ ದರ",
    "owner_analytics.recent_activity": "ಇತ್ತೀಚಿನ ಚಟುವಟಿಕೆ",
    "owner_analytics.recent_activity_subtitle": "ಮನೆಗಳಿಂದ ಲೈವ್ ಅಪ್‌ಡೇಟ್‌ಗಳು",
    "owner_analytics.user_growth": "ಸಮಯದೊಂದಿಗೆ ಬಳಕೆದಾರ ಬೆಳವಣಿಗೆ",
    "owner_analytics.user_growth_subtitle": "ಹೊಸ ನೋಂದಣಿ ಮತ್ತು ಮನೆ ಬೆಳವಣಿಗೆ",
    "owner_analytics.chart_placeholder": "ಇಲ್ಲಿ ಚಾರ್ಟ್ ಕಾಣಿಸುತ್ತದೆ",
    "common.cancel": "ರದ್ದುಮಾಡಿ",
    "common.close": "ಮುಚ್ಚಿ",
    "common.save": "ಉಳಿಸಿ",
    "common.done": "ಮುಗಿದಿದೆ",
    "common.add": "ಸೇರಿಸಿ",
    "common.edit": "ಸಂಪಾದಿಸಿ",
    "common.delete": "ಅಳಿಸಿ",
    "common.search": "ಹುಡುಕಿ",
    "common.clear": "ತೆರವುಗೊಳಿಸಿ",
    "common.start": "ಪ್ರಾರಂಭಿಸಿ",
    "common.end": "ಕೊನೆ",
    "common.create_failed": "ರಚಿಸಲು ವಿಫಲವಾಯಿತು",
    "common.update_failed": "ನವೀಕರಣ ವಿಫಲವಾಯಿತು",
    "common.delete_failed": "ಅಳಿಸಲು ವಿಫಲವಾಯಿತು",
    "common.missing_session": "ಸೆಷನ್ ಟೋಕನ್ ಅಥವಾ ಮನೆ ಐಡಿ ಲಭ್ಯವಿಲ್ಲ.",
    "common.unknown": "ಅಜ್ಞಾತ",
    "common.unknown_error": "ಅಜ್ಞಾತ ದೋಷ",
    "weekday.mon": "ಸೋಮ",
    "weekday.tue": "ಮಂಗಳ",
    "weekday.wed": "ಬುಧ",
    "weekday.thu": "ಗುರು",
    "weekday.fri": "ಶುಕ್ರ",
    "weekday.sat": "ಶನಿ",
    "weekday.sun": "ರವಿ",
    "common.loading": "ಲೋಡ್ ಆಗುತ್ತಿದೆ…",
    "common.or": "ಅಥವಾ",
    "common.logout": "ಲಾಗ್ ಔಟ್",
    "common.refresh": "ರಿಫ್ರೆಶ್",
    "layout.household_manager": "ಮನೆ ನಿರ್ವಾಹಕ",
    "layout.role_demo": "ಪಾತ್ರ (ಡೆಮೋ)",
    "layout.role.household": "ಮನೆ ಬಳಕೆದಾರ",
    "layout.role.admin": "ನಿರ್ವಾಹಕ",
    "layout.role.owner": "ಮಾಲೀಕ / ಡೆವ್",
    "layout.role.support": "ಬೆಂಬಲ ಸಿಬ್ಬಂದಿ",
    "layout.agent_setup": "ಏಜೆಂಟ್ ಸೆಟಪ್",
    "layout.profile": "ಪ್ರೊಫೈಲ್",
    "layout.send_feedback": "ಪ್ರತಿಕ್ರಿಯೆ ಕಳುಹಿಸಿ",
    "layout.chat": "ಚಾಟ್",
    "layout.chat_assistant": "ಚಾಟ್ ಸಹಾಯಕ",
    "signals.title": "ಸಿಗ್ನಲ್‌ಗಳು",
    "signals.subtitle": "ಚಾಟ್ ಮತ್ತು ಆಟೋಮೇಷನ್‌ಗಳಿಂದ ಸೆರೆಹಿಡಿದ ಮನೆಯ ಘಟನೆಗಳು ಮತ್ತು ಪ್ರತಿಕ್ರಿಯೆ",
    "signals.upcoming_events": "ಮುಂಬರುವ ಘಟನೆಗಳು",
    "signals.no_upcoming_events": "ಮುಂಬರುವ ಘಟನೆಗಳಿಲ್ಲ",
    "signals.cleaning_feedback": "ಸ್ವಚ್ಛತೆಯ ಪ್ರತಿಕ್ರಿಯೆ",
    "signals.no_cleaning_feedback": "ಇನ್ನೂ ಸ್ವಚ್ಛತೆಯ ಪ್ರತಿಕ್ರಿಯೆ ಇಲ್ಲ",
    "signals.rating": "ರೇಟಿಂಗ್",
    "automations.title": "ಆಟೋಮೇಷನ್‌ಗಳು",
    "automations.subtitle": "ನಿಗದಿತ ಜ್ಞಾಪನೆಗಳು ಮತ್ತು ಸಕ್ರಿಯ ಎಚ್ಚರಿಕೆಗಳನ್ನು ನಿರ್ವಹಿಸಿ",
    "automations.summary": "ಸಕ್ರಿಯ {active} • ವಿರಾಮ {paused} • ನಿಷ್ಕ್ರಿಯ {disabled}",
    "automations.none_title": "ಇನ್ನೂ ಆಟೋಮೇಷನ್‌ಗಳಿಲ್ಲ",
    "automations.none_body": "ನಿಗದಿತ ಎಚ್ಚರಿಕೆಗಳನ್ನು ರಚಿಸಲು ಚಾಟ್‌ನಿಂದ ಆಟೋಮೇಷನ್ ಸಲಹೆಯನ್ನು ಅನುಮೋದಿಸಿ.",
    "automations.cadence": "ಆವರ್ತನ",
    "automations.next_run": "ಮುಂದಿನ ರನ್",
    "automations.last_run": "ಕೊನೆಯ ರನ್",
    "automations.run_now": "ಈಗ ರನ್ ಮಾಡಿ",
    "automations.pause": "ವಿರಾಮ",
    "automations.resume": "ಮುಂದುವರಿಸಿ",
    "automations.disable": "ನಿಷ್ಕ್ರಿಯಗೊಳಿಸಿ",
    "automations.edit": "ಆಟೋಮೇಷನ್ ಸಂಪಾದಿಸಿ",
    "automations.field_title": "ಶೀರ್ಷಿಕೆ",
    "automations.field_description": "ವಿವರಣೆ",
    "automations.field_cadence": "ಆವರ್ತನ",
    "automations.field_at_time": "ಸಮಯದಲ್ಲಿ (ಐಚ್ಛಿಕ)",
    "automations.field_day_of_week": "ವಾರದ ದಿನ (ವಾರಕ್ಕೊಮ್ಮೆ)",
    "automations.field_day_of_month": "ತಿಂಗಳ ದಿನ (ತಿಂಗಳಿಗೊಮ್ಮೆ)",
    "automations.cadence_daily": "ಪ್ರತಿದಿನ",
    "automations.cadence_weekdays": "ಕೆಲಸದ ದಿನಗಳು",
    "automations.cadence_weekly": "ವಾರಕ್ಕೊಮ್ಮೆ",
    "automations.cadence_monthly": "ತಿಂಗಳಿಗೊಮ್ಮೆ",
    "automations.cadence_hourly": "ಗಂಟೆಗೊಮ್ಮೆ",
    "automations.cadence_every_2_hours": "ಪ್ರತಿ 2 ಗಂಟೆಗೊಮ್ಮೆ",
    "automations.cadence_every_5_minutes": "ಪ್ರತಿ 5 ನಿಮಿಷಕ್ಕೊಮ್ಮೆ",
    "automations.status_active": "ಸಕ್ರಿಯ",
    "automations.status_paused": "ವಿರಾಮಗೊಂಡಿದೆ",
    "automations.status_disabled": "ನಿಷ್ಕ್ರಿಯ",
    "automations.title_required": "ಶೀರ್ಷಿಕೆ ಅಗತ್ಯ",
    "automations.weekly_requires_dow": "ವಾರಕ್ಕೊಮ್ಮೆ ಆವರ್ತನಕ್ಕೆ day_of_week ಅಗತ್ಯ",
    "automations.monthly_requires_dom": "ತಿಂಗಳಿಗೊಮ್ಮೆ ಆವರ್ತನಕ್ಕೆ day_of_month ಅಗತ್ಯ",
    "auth.login.welcome_back": "ಮರಳಿ ಸ್ವಾಗತ",
    "auth.login.subtitle": "ನಿಮ್ಮ Home Operations Manager‌ಗೆ ಸೈನ್ ಇನ್ ಮಾಡಿ",
    "auth.login.email": "ಇಮೇಲ್",
    "auth.login.password": "ಪಾಸ್‌ವರ್ಡ್",
    "auth.login.remember_me": "ನನ್ನನ್ನು ನೆನಪಿಡಿ",
    "auth.login.forgot_password": "ಪಾಸ್‌ವರ್ಡ್ ಮರೆತಿರಾ?",
    "auth.login.sign_in": "ಸೈನ್ ಇನ್",
    "auth.login.google": "Google",
    "auth.login.microsoft": "Microsoft",
    "auth.login.no_account": "ಖಾತೆ ಇಲ್ಲವೇ?",
    "auth.login.sign_up": "ಸೈನ್ ಅಪ್",
    "auth.signup.create_account": "ಖಾತೆ ರಚಿಸಿ",
    "auth.signup.subtitle": "ನಿಮ್ಮ ಮನೆಗೆ ಸೇರಿ ಅಥವಾ ನಿಮ್ಮ ಮನೆಯ ನಿರ್ವಹಣೆ ಪ್ರಾರಂಭಿಸಿ",
    "auth.signup.full_name": "ಪೂರ್ಣ ಹೆಸರು",
    "auth.signup.household_code": "ಮನೆ ಕೋಡ್ (ಐಚ್ಛಿಕ)",
    "auth.signup.household_code_placeholder": "ಅಸ್ತಿತ್ವದ ಮನೆ ಕೋಡ್ ನಮೂದಿಸಿ",
    "auth.signup.household_code_help": "ಹೊಸ ಮನೆ ರಚಿಸಲು ಖಾಲಿ ಬಿಡಿ",
    "auth.signup.confirm_password": "ಪಾಸ್‌ವರ್ಡ್ ದೃಢೀಕರಿಸಿ",
    "auth.signup.terms": "ನಾನು ಸೇವಾ ನಿಯಮಗಳು ಮತ್ತು ಗೌಪ್ಯತೆ ನೀತಿಗೆ ಒಪ್ಪುತ್ತೇನೆ",
    "auth.signup.create": "ಖಾತೆ ರಚಿಸಿ",
    "auth.signup.have_account": "ಈಗಾಗಲೇ ಖಾತೆ ಇದೆಯೇ?",
    "auth.signup.sign_in": "ಸೈನ್ ಇನ್",
    "dashboard.title": "ಡ್ಯಾಶ್‌ಬೋರ್ಡ್",
    "dashboard.subtitle": "ಮರಳಿ ಸ್ವಾಗತ! ನಿಮ್ಮ ಮನೆಯಲ್ಲಿ ಏನು ನಡೆಯುತ್ತಿದೆ ಎಂಬುದನ್ನು ಇಲ್ಲಿ ನೋಡಿ.",
    "dashboard.setup_incomplete": "ನಿಮ್ಮ ಮನೆ ಸೆಟಪ್ ಅಪೂರ್ಣವಾಗಿದೆ",
    "dashboard.continue_setup": "ಸೆಟಪ್ ಮುಂದುವರಿಸಿ",
    "dashboard.setup_home_profile": "ಮನೆ ಪ್ರೊಫೈಲ್",
    "dashboard.setup_features": "ಮನೆ ವೈಶಿಷ್ಟ್ಯಗಳು",
    "dashboard.setup_chores": "ಕೆಲಸಗಳು",
    "dashboard.setup_helpers": "ಸಹಾಯಕರು",
    "dashboard.assign_chores_title": "ಕೆಲಸಗಳನ್ನು ನಿಯೋಜಿಸಬೇಕು",
    "dashboard.assign_chores_hint": "{count} ಕೆಲಸಗಳನ್ನು ಇನ್ನೂ ಯಾವುದೇ ಸಹಾಯಕರಿಗೆ ನಿಯೋಜಿಸಿಲ್ಲ.",
    "dashboard.assign_chores_button": "ಸಹಾಯಕರಿಗೆ ನಿಯೋಜಿಸಿ",
    "dashboard.upcoming_chores": "ಮುಂಬರುವ ಕೆಲಸಗಳು",
    "dashboard.tasks_this_week": "ಈ ವಾರದ ಕಾರ್ಯಗಳು",
    "dashboard.view_all": "ಎಲ್ಲವನ್ನೂ ನೋಡಿ",
    "dashboard.no_upcoming_chores": "ಮುಂಬರುವ ಕೆಲಸಗಳಿಲ್ಲ.",
    "dashboard.recent_alerts": "ಇತ್ತೀಚಿನ ಎಚ್ಚರಿಕೆಗಳು",
    "dashboard.notifications_and_reminders": "ಅಧಿಸೂಚನೆಗಳು ಮತ್ತು ಜ್ಞಾಪನೆಗಳು",
    "dashboard.progress_title": "ಮಾರ್ಚ್ ಪ್ರಗತಿ",
    "dashboard.progress_subtitle": "ಈ ತಿಂಗಳ ಕಾರ್ಯ ಪೂರ್ಣಗೊಳಿಸುವಿಕೆ",
    "dashboard.completed": "ಪೂರ್ಣಗೊಂಡಿದೆ",
    "dashboard.in_progress": "ಪ್ರಗತಿಯಲ್ಲಿದೆ",
    "dashboard.overdue": "ಗಡುವು ಮೀರಿದೆ",
    "dashboard.quick_actions": "ತ್ವರಿತ ಕ್ರಿಯೆಗಳು",
    "dashboard.add_chore": "ಕೆಲಸ ಸೇರಿಸಿ",
    "dashboard.add_recipe": "ರೆಸಿಪಿ ಸೇರಿಸಿ",
    "dashboard.chat_assistant": "ಚಾಟ್ ಸಹಾಯಕ",
    "dashboard.view_status": "ಸ್ಥಿತಿ ನೋಡಿ",
    "tests.title": "ಪರೀಕ್ಷೆಗಳು",
    "tests.subtitle": "ನಿಮ್ಮ ಸ್ವಯಂಚಾಲಿತ ಪರೀಕ್ಷಾ ಸೂಟ್‌ಗಳನ್ನು ರನ್ ಮಾಡಿ ಮತ್ತು ನಿಗದಿಪಡಿಸಿ",
    "tests.run_now": "ಈಗ ರನ್ ಮಾಡಿ",
    "tests.run_now_help": "ಪರೀಕ್ಷೆ ರನ್ ಪ್ರಾರಂಭಿಸಿ ಮತ್ತು ಫಲಿತಾಂಶಗಳು ಮತ್ತು ಲಾಗ್‌ಗಳನ್ನು ಟ್ರ್ಯಾಕ್ ಮಾಡಿ",
    "tests.run_vitest": "Vitest ರನ್ ಮಾಡಿ",
    "tests.run_vitest_watch": "Vitest (ವಾಚ್)",
    "tests.run_vitest_ui": "Vitest UI",
    "tests.run_playwright": "Playwright ರನ್ ಮಾಡಿ",
    "tests.run_playwright_ui": "Playwright UI",
    "tests.schedules": "ವೇಳಾಪಟ್ಟಿಗಳು",
    "tests.schedules_help": "ಕ್ರಾನ್ ವೇಳಾಪಟ್ಟಿಗಳನ್ನು ನಿಮ್ಮ ಸ್ಥಳೀಯ ಟೆಸ್ಟ್ ರನ್ನರ್ ಸೇವೆಯಿಂದ ಕಾರ್ಯಗತಗೊಳಿಸಲಾಗುತ್ತದೆ",
    "tests.schedule_name": "ವೇಳಾಪಟ್ಟಿ ಹೆಸರು",
    "tests.cron_expr": "ಕ್ರಾನ್ ಎಕ್ಸ್‌ಪ್ರೆಷನ್",
    "tests.type": "ಪ್ರಕಾರ",
    "tests.mode": "ಮೋಡ್",
    "tests.add_schedule": "ವೇಳಾಪಟ್ಟಿ ಸೇರಿಸಿ",
    "tests.no_schedules": "ಇನ್ನೂ ವೇಳಾಪಟ್ಟಿಗಳಿಲ್ಲ.",
    "tests.last_run": "ಕೊನೆಯ ರನ್",
    "tests.enable": "ಸಕ್ರಿಯಗೊಳಿಸಿ",
    "tests.disable": "ನಿಷ್ಕ್ರಿಯಗೊಳಿಸಿ",
    "tests.recent_runs": "ಇತ್ತೀಚಿನ ರನ್‌ಗಳು",
    "tests.recent_runs_help": "ಸ್ಥಳೀಯ ರನ್ನರ್‌ನಿಂದ ಸೆರೆಹಿಡಿದ ಇತ್ತೀಚಿನ ಪರೀಕ್ಷಾ ರನ್‌ಗಳು",
    "tests.no_runs": "ಇನ್ನೂ ರನ್‌ಗಳಿಲ್ಲ.",
    "tests.created": "ರಚಿಸಲಾಗಿದೆ",
    "tests.started": "ಪ್ರಾರಂಭವಾಗಿದೆ",
    "tests.finished": "ಮುಗಿದಿದೆ",
    "tests.view_log": "ಲಾಗ್ ನೋಡಿ",
    "tests.view_report": "ವರದಿ ನೋಡಿ",
    "tests.cancel": "ರದ್ದುಮಾಡಿ",
    "recipes.title": "ರೆಸಿಪಿಗಳು",
    "recipes.subtitle": "ನಿಮ್ಮ ಕುಟುಂಬದ ನೆಚ್ಚಿನ ಊಟಗಳು ಮತ್ತು ಅಡುಗೆ ಐಡಿಯಾಗಳು",
    "recipes.add_recipe": "ರೆಸಿಪಿ ಸೇರಿಸಿ",
    "recipes.youtube_search": "YouTube ರೆಸಿಪಿ ಹುಡುಕಾಟ",
    "recipes.youtube_search_subtitle": "ಕಸ್ಟಮೈಸ್ ಮಾಡಬಹುದಾದ ಕ್ವೆರಿ ಆಯ್ಕೆಗಳೊಂದಿಗೆ YouTube‌ನಲ್ಲಿ ರೆಸಿಪಿಗಳನ್ನು ಹುಡುಕಿ",
    "recipes.search_options": "ಹುಡುಕಾಟ ಆಯ್ಕೆಗಳು",
    "recipes.recipe": "ರೆಸಿಪಿ",
    "recipes.generated_query": "ಸೃಷ್ಟಿಸಿದ ಕ್ವೆರಿ",
    "recipes.search_youtube": "YouTube‌ನಲ್ಲಿ ಹುಡುಕಿ",
    "recipes.use_current_search": "ಪ್ರಸ್ತುತ ಪುಟ ಹುಡುಕಾಟವನ್ನು ಬಳಸಿ",
    "recipes.search_placeholder": "ಹೆಸರು ಅಥವಾ ಪಾಕಪದ್ಧತಿಯಿಂದ ರೆಸಿಪಿಗಳನ್ನು ಹುಡುಕಿ...",
    "recipes.category": "ವರ್ಗ",
    "recipes.all": "ಎಲ್ಲಾ",
    "recipes.breakfast": "ಬೆಳಗಿನ ಉಪಾಹಾರ",
    "recipes.lunch": "ಮಧ್ಯಾಹ್ನದ ಊಟ",
    "recipes.dinner": "ರಾತ್ರಿಯ ಊಟ",
    "recipes.dessert": "ಸಿಹಿತಿಂಡಿ",
    "recipes.ingredients": "ಪದಾರ್ಥಗಳು:",
    "recipes.more": "ಇನ್ನಷ್ಟು...",
    "recipes.view_full": "ಪೂರ್ಣ ರೆಸಿಪಿ ನೋಡಿ",
    "recipes.no_recipes": "ಯಾವುದೇ ರೆಸಿಪಿಗಳು ಸಿಗಲಿಲ್ಲ",
    "recipes.no_recipes_hint": "ನಿಮ್ಮ ಹುಡುಕಾಟವನ್ನು ಸರಿಹೊಂದಿಸಲು ಪ್ರಯತ್ನಿಸಿ ಅಥವಾ ಹೊಸ ರೆಸಿಪಿ ಸೇರಿಸಿ",
    "recipes.add_new_recipe": "ಹೊಸ ರೆಸಿಪಿ ಸೇರಿಸಿ",
    "recipes.recipe_title": "ರೆಸಿಪಿ ಶೀರ್ಷಿಕೆ",
    "recipes.description": "ವಿವರಣೆ",
    "recipes.cuisine": "ಪಾಕಪದ್ಧತಿ",
    "recipes.prep_time": "ಸಿದ್ಧತಾ ಸಮಯ",
    "recipes.cook_time": "ಅಡುಗೆ ಸಮಯ",
    "recipes.servings": "ಪ್ರಮಾಣ",
    "recipes.instructions": "ಸೂಚನೆಗಳು",
    "recipes.save_recipe": "ರೆಸಿಪಿ ಉಳಿಸಿ",
    "recipes.youtube_options": "YouTube ಹುಡುಕಾಟ ಆಯ್ಕೆಗಳು",
    "recipes.query_template": "ಕ್ವೆರಿ ಟೆಂಪ್ಲೇಟ್",
    "recipes.preferred_channels": "ಆದ್ಯತೆಯ ಚಾನಲ್‌ಗಳು / ಕೀವರ್ಡ್‌ಗಳು (ಐಚ್ಛಿಕ)",
    "recipes.include_shorts": "Shorts ಸೇರಿಸಿ",
    "recipes.no": "ಇಲ್ಲ",
    "recipes.yes": "ಹೌದು",
    "recipes.tab_recipes": "ಅಡುಗೆಗಳು",
    "recipes.tab_meal_plan": "ಊಟದ ಯೋಜನೆ",
    "recipes.tab_shopping_list": "ಖರೀದಿ ಪಟ್ಟಿ",
    "recipes.edit_recipe": "ಅಡುಗೆ ಸಂಪಾದಿಸಿ",
    "recipes.source_url": "ಮೂಲ URL",
    "recipes.ingredients_hint": "ಪ್ರತಿ ಸಾಲಿಗೆ ಒಂದು ಪದಾರ್ಥ",
    "recipes.prev_week": "ಹಿಂದಿನ ವಾರ",
    "recipes.next_week": "ಮುಂದಿನ ವಾರ",
    "recipes.assign_meal": "ಊಟವನ್ನು ಯೋಜಿಸಿ",
    "recipes.date": "ದಿನಾಂಕ",
    "recipes.meal_type": "ಊಟ",
    "recipes.pick_recipe": "ಅಡುಗೆ ಆಯ್ಕೆಮಾಡಿ",
    "recipes.or": "ಅಥವಾ",
    "recipes.custom_meal": "ಕಸ್ಟಮ್ ಊಟ",
    "recipes.notes": "ಟಿಪ್ಪಣಿಗಳು",
    "recipes.shopping_list_hint": "ಈ ವಾರದ ಊಟದ ಯೋಜನೆಗೆ ಬೇಕಾದ ಪದಾರ್ಥಗಳು, ನಿಮ್ಮ ಪ್ಯಾಂಟ್ರಿಯೊಂದಿಗೆ ಹೋಲಿಕೆ",
    "recipes.shopping_list_empty": "ಇನ್ನೂ ಯಾವುದೇ ವಸ್ತುಗಳಿಲ್ಲ. ಖರೀದಿ ಪಟ್ಟಿ ರಚಿಸಲು ವಾರದ ಊಟಗಳನ್ನು ಯೋಜಿಸಿ.",
    "alerts.no_alerts": "ಯಾವುದೇ ಎಚ್ಚರಿಕೆಗಳಿಲ್ಲ",
    "alerts.caught_up": "ನೀವು ಎಲ್ಲವನ್ನೂ ಪೂರ್ಣಗೊಳಿಸಿದ್ದೀರಿ!",
    "alerts.title": "ಎಚ್ಚರಿಕೆಗಳು ಮತ್ತು ಅಧಿಸೂಚನೆಗಳು",
    "alerts.subtitle": "ಮನೆಯ ಚಟುವಟಿಕೆಗಳು ಮತ್ತು ಜ್ಞಾಪನೆಗಳ ಬಗ್ಗೆ ಮಾಹಿತಿ ಪಡೆಯಿರಿ",
    "alerts.unread": "ಓದದ",
    "alerts.mark_all_read": "ಎಲ್ಲವನ್ನೂ ಓದಿದಂತೆ ಗುರುತಿಸಿ",
    "alerts.all": "ಎಲ್ಲಾ",
    "alerts.critical_tab": "ಗಂಭೀರ",
    "alerts.warning_tab": "ಎಚ್ಚರಿಕೆ",
    "alerts.info_tab": "ಮಾಹಿತಿ",
    "alerts.create_custom": "ಕಸ್ಟಮ್ ಎಚ್ಚರಿಕೆ ರಚಿಸಿ",
    "alerts.alert_title": "ಎಚ್ಚರಿಕೆ ಶೀರ್ಷಿಕೆ",
    "alerts.message": "ಸಂದೇಶ",
    "alerts.priority": "ಆದ್ಯತೆ",
    "alerts.category": "ವರ್ಗ",
    "alerts.critical": "ಗಂಭೀರ",
    "alerts.warning": "ಎಚ್ಚರಿಕೆ",
    "alerts.info": "ಮಾಹಿತಿ",
    "alerts.maintenance": "ನಿರ್ವಹಣೆ",
    "alerts.bills": "ಬಿಲ್‌ಗಳು",
    "alerts.inventory": "ದಾಸ್ತಾನು",
    "alerts.safety": "ಸುರಕ್ಷತೆ",
    "alerts.reminders": "ಜ್ಞಾಪನೆಗಳು",
    "alerts.alert_date_time": "ಎಚ್ಚರಿಕೆ ದಿನಾಂಕ ಮತ್ತು ಸಮಯ",
    "alerts.create": "ಎಚ್ಚರಿಕೆ ರಚಿಸಿ",
    "admin.title": "ನಿರ್ವಾಹಕ ಸಂರಚನೆ",
    "admin.subtitle": "ಮನೆ ಸೆಟ್ಟಿಂಗ್‌ಗಳು ಮತ್ತು ಆದ್ಯತೆಗಳನ್ನು ನಿರ್ವಹಿಸಿ",
    "admin.general": "ಸಾಮಾನ್ಯ",
    "admin.members": "ಸದಸ್ಯರು",
    "admin.notifications": "ಅಧಿಸೂಚನೆಗಳು",
    "admin.security": "ಸುರಕ್ಷತೆ",
    "admin.integrations": "ಏಕೀಕರಣಗಳು",
    "admin.agents": "ಏಜೆಂಟ್‌ಗಳು",
    "admin.agents_subtitle": "ಅಂತರ್ನಿರ್ಮಿತ ಏಜೆಂಟ್‌ಗಳನ್ನು ನಿರ್ವಹಿಸಿ (ಮನೆ ಓವರ್‌ರೈಡ್‌ಗಳು)",
    "admin.agents_empty": "ಯಾವುದೇ ಏಜೆಂಟ್‌ಗಳು ಸಿಗಲಿಲ್ಲ",
    "admin.agent_enabled": "ಸಕ್ರಿಯಗೊಂಡಿದೆ",
    "admin.agent_key": "ಕೀ",
    "admin.agent_display_name": "ಪ್ರದರ್ಶನ ಹೆಸರು",
    "admin.agent_model": "ಮಾದರಿ (ಐಚ್ಛಿಕ)",
    "admin.agent_system_prompt": "ಸಿಸ್ಟಮ್ ಪ್ರಾಂಪ್ಟ್",
    "admin.agent_tool_allowlist": "ಟೂಲ್ ಅನುಮತಿ ಪಟ್ಟಿ (ಅಲ್ಪವಿರಾಮದಿಂದ ಬೇರ್ಪಟ್ಟ)",
    "admin.agent_tool_allowlist_help": "ಉದಾಹರಣೆ: db.select, db.insert",
    "admin.members_subtitle": "ಮನೆ ಸದಸ್ಯರನ್ನು ನೋಡಿ",
    "admin.members_empty": "ಯಾವುದೇ ಸದಸ್ಯರು ಸಿಗಲಿಲ್ಲ",
    "admin.notifications_subtitle": "ಅಧಿಸೂಚನೆ ಆದ್ಯತೆಗಳು",
    "admin.notifications_enabled": "ಅಧಿಸೂಚನೆಗಳು ಸಕ್ರಿಯಗೊಂಡಿವೆ",
    "admin.email_digest": "ಇಮೇಲ್ ಡೈಜೆಸ್ಟ್",
    "admin.security_subtitle": "ಸುರಕ್ಷತೆ ಆದ್ಯತೆಗಳು",
    "admin.two_factor_auth": "ಎರಡು ಅಂಶ ದೃಢೀಕರಣ",
    "admin.auto_assign_chores": "ಕೆಲಸಗಳನ್ನು ಸ್ವಯಂ ನಿಯೋಜಿಸಿ",
    "admin.integrations_subtitle": "ಏಕೀಕರಣ ಸೆಟ್ಟಿಂಗ್‌ಗಳು",
    "admin.youtube_settings": "YouTube ಸೆಟ್ಟಿಂಗ್‌ಗಳು",
    "admin.recipe_settings": "ರೆಸಿಪಿ ಸೆಟ್ಟಿಂಗ್‌ಗಳು",
    "admin.allowed_sources": "ಅನುಮತಿಸಲಾದ ಮೂಲಗಳು (ಅಲ್ಪವಿರಾಮದಿಂದ ಬೇರ್ಪಟ್ಟ)",
    "admin.min_rating": "ಕನಿಷ್ಠ ರೇಟಿಂಗ್",
    "admin.min_reviews": "ಕನಿಷ್ಠ ವಿಮರ್ಶೆಗಳು",
    "admin.lenient_missing_reviews": "ವಿಮರ್ಶೆಗಳು ಲಭ್ಯವಿಲ್ಲದಿದ್ದಾಗ ಸಡಿಲ",
    "admin.invite_email": "ಆಮಂತ್ರಣ ಇಮೇಲ್",
    "admin.invite_role": "ಆಮಂತ್ರಣ ಪಾತ್ರ",
    "admin.role_member": "ಸದಸ್ಯ",
    "admin.role_admin": "ನಿರ್ವಾಹಕ",
    "admin.role_owner": "ಮಾಲೀಕ",
    "admin.send_invite": "ಆಮಂತ್ರಣ ಕಳುಹಿಸಿ",
    "admin.pending_invites": "ಬಾಕಿ ಇರುವ ಆಮಂತ್ರಣಗಳು",
    "admin.invites_empty": "ಯಾವುದೇ ಆಮಂತ್ರಣಗಳಿಲ್ಲ",
    "admin.copy_invite_link": "ಲಿಂಕ್ ನಕಲಿಸಿ",
    "admin.revoke_invite": "ರದ್ದುಗೊಳಿಸಿ",
    "admin.invite_status_pending": "ಬಾಕಿ ಇದೆ",
    "admin.invite_status_accepted": "ಸ್ವೀಕರಿಸಲಾಗಿದೆ",
    "admin.invite_status_revoked": "ರದ್ದುಗೊಳಿಸಲಾಗಿದೆ",
    "invite.accept.title": "ಆಮಂತ್ರಣ ಸ್ವೀಕರಿಸಿ",
    "invite.accept.subtitle": "ಮನೆಗೆ ಸೇರಿ",
    "invite.accept.missing_token": "ಆಮಂತ್ರಣ ಟೋಕನ್ ಲಭ್ಯವಿಲ್ಲ",
    "invite.accept.working": "ಆಮಂತ್ರಣ ಸ್ವೀಕರಿಸಲಾಗುತ್ತಿದೆ…",
    "invite.accept.success": "ಆಮಂತ್ರಣ ಸ್ವೀಕರಿಸಲಾಗಿದೆ. ನೀವು ಈಗ ಮನೆಯ ಸದಸ್ಯರಾಗಿದ್ದೀರಿ.",
    "invite.accept.go_home": "ಡ್ಯಾಶ್‌ಬೋರ್ಡ್‌ಗೆ ಹೋಗಿ",
    "admin.household_information": "ಮನೆ ಮಾಹಿತಿ",
    "admin.household_information_subtitle": "ನಿಮ್ಮ ಮನೆಯ ಬಗ್ಗೆ ಮೂಲ ಮಾಹಿತಿ",
    "admin.household_name": "ಮನೆ ಹೆಸರು",
    "admin.address": "ವಿಳಾಸ",
    "admin.timezone": "ಸಮಯ ವಲಯ",
    "admin.language": "ಭಾಷೆ",
    "admin.save_changes": "ಬದಲಾವಣೆಗಳನ್ನು ಉಳಿಸಿ",
    "support.tickets": "ಬೆಂಬಲ ಟಿಕೆಟ್‌ಗಳು",
    "support.subheader": "ಎಲ್ಲಾ ಬಳಕೆದಾರ ಬೆಂಬಲ ವಿನಂತಿಗಳು",
    "support.title": "ಬೆಂಬಲ ಪ್ಯಾನೆಲ್",
    "support.subtitle": "ಬಳಕೆದಾರ ಬೆಂಬಲ ವಿನಂತಿಗಳನ್ನು ನಿರ್ವಹಿಸಿ ಮತ್ತು ಪ್ರತಿಕ್ರಿಯಿಸಿ",
    "support.open_tickets": "ತೆರೆದ ಟಿಕೆಟ್‌ಗಳು",
    "support.in_progress": "ಪ್ರಗತಿಯಲ್ಲಿದೆ",
    "support.resolved_today": "ಇಂದು ಪರಿಹರಿಸಲಾಗಿದೆ",
    "support.avg_response_time": "ಸರಾಸರಿ ಪ್ರತಿಕ್ರಿಯೆ ಸಮಯ",
    "support.search_placeholder": "ಟಿಕೆಟ್‌ಗಳನ್ನು ಹುಡುಕಿ...",
    "support.all": "ಎಲ್ಲಾ",
    "support.open": "ತೆರೆದಿದೆ",
    "support.resolved": "ಪರಿಹರಿಸಲಾಗಿದೆ",
    "support.type_response": "ನಿಮ್ಮ ಪ್ರತಿಕ್ರಿಯೆ ಟೈಪ್ ಮಾಡಿ...",
    "chat.review_home_profile": "ಮನೆ ಪ್ರೊಫೈಲ್ ಪರಿಶೀಲಿಸಿ",
    "chat.recommended_chores": "ಶಿಫಾರಸು ಮಾಡಿದ ಕೆಲಸಗಳು",
    "chat.recommended_chores_help": "ನಿಮಗೆ ಬೇಕಾದದನ್ನು ಆಯ್ಕೆ ಮಾಡಿ, ಅಗತ್ಯವಿದ್ದರೆ ಮಾತ್ರ ಸಂಪಾದಿಸಿ, ನಂತರ ಸಲ್ಲಿಸಿ.",
    "chat.recommended_automations": "ಶಿಫಾರಸು ಮಾಡಿದ ಆಟೋಮೇಷನ್‌ಗಳು",
    "chat.recommended_automations_help": "ಪರಿಶೀಲಿಸಿ ಮತ್ತು ನಿಗದಿತ ಎಚ್ಚರಿಕೆಗಳಾಗಿ ಪರಿವರ್ತಿಸಲು ಅನುಮೋದಿಸಿ.",
    "chat.toggle_all": "ಎಲ್ಲವನ್ನೂ ಟಾಗಲ್ ಮಾಡಿ",
    "chat.submit": "ಸಲ್ಲಿಸಿ",
    "chat.edit_chore": "ಕೆಲಸ ಸಂಪಾದಿಸಿ",
    "chat.chore_title": "ಶೀರ್ಷಿಕೆ",
    "chat.chore_description": "ವಿವರಣೆ",
    "chat.frequency": "ಆವರ್ತನ",
    "chat.frequency_not_set": "ಸೆಟ್ ಮಾಡಿಲ್ಲ",
    "chat.frequency_daily": "ಪ್ರತಿದಿನ",
    "chat.frequency_weekly": "ವಾರಕ್ಕೊಮ್ಮೆ",
    "chat.frequency_biweekly": "ಎರಡು ವಾರಕ್ಕೊಮ್ಮೆ",
    "chat.frequency_monthly": "ತಿಂಗಳಿಗೊಮ್ಮೆ",
    "chat.due": "ಗಡುವು",
    "chat.helper": "ಹೆಲ್ಪರ್",
    "chat.unassigned": "ನಿಯೋಜಿಸದ",
    "chat.approve": "ಅನುಮೋದಿಸಿ",
    "chat.reject": "ತಿರಸ್ಕರಿಸಿ",
    "chat.apply": "ಅನ್ವಯಿಸಿ",
    "chat.create_table": "{table} ರಚಿಸಿ",
    "chat.tool_execution_failed": "ಟೂಲ್ ಕಾರ್ಯಗತಗೊಳಿಸುವಿಕೆ ವಿಫಲ",
    "chat.executed_tool": "{tool} ಕಾರ್ಯಗತಗೊಳಿಸಲಾಗಿದೆ.",
    "chat.create_home_profile_first": "ದಯವಿಟ್ಟು ಮೊದಲು ನಿಮ್ಮ ಮನೆ ಪ್ರೊಫೈಲ್ ರಚಿಸಿ, ನಂತರ ಶಿಫಾರಸುಗಳನ್ನು ರಚಿಸಿ.",
    "chat.could_not_fetch": "ಮಾಹಿತಿಯನ್ನು ಪಡೆಯಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ",
    "chat.transliteration_off": "ABC",
    "helper_daily.empty_hint": "ಈ ದಿನಾಂಕಕ್ಕೆ ಯಾವುದೇ ಕೆಲಸಗಳನ್ನು ನಿಗದಿಪಡಿಸಿಲ್ಲ. ಕೆಲಸಗಳನ್ನು ರಚಿಸಲು Sync Schedule ಬಳಸಿ, ಅಥವಾ ಎಲ್ಲಾ ಕೆಲಸಗಳನ್ನು ನೋಡಲು ಲಿಸ್ಟ್ ವೀವ್‌ಗೆ ಬದಲಾಯಿಸಿ.",
  },
};

const I18nContext = createContext<I18nContextValue | null>(null);

function normalizeLang(input: string): UiLanguage {
  if (input === "hi") return "hi";
  if (input === "kn") return "kn";
  return "en";
}

export function UiLanguageProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<UiLanguage>(() => {
    try {
      return normalizeLang(localStorage.getItem(STORAGE_KEY) ?? "en");
    } catch {
      return "en";
    }
  });

  const setLang = useCallback((next: UiLanguage) => {
    setLangState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // ignore
    }
  }, [lang]);

  const t = useCallback(
    (key: string) => {
      const table = TRANSLATIONS[lang] ?? TRANSLATIONS.en;
      if (key in table) return table[key];
      const fallback = TRANSLATIONS.en;
      if (key in fallback) return fallback[key];
      return key;
    },
    [lang],
  );

  const value = useMemo<I18nContextValue>(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (ctx) return ctx;
  return {
    lang: "en",
    setLang: () => {
      return;
    },
    t: (key: string) => {
      const fallback = TRANSLATIONS.en;
      if (key in fallback) return fallback[key];
      return key;
    },
  };
}
